import React, { useEffect, useMemo, useState } from "react";
import api from "../axiosConfig";
import recoveredTemplates from "../data/recoveredGeneratorTemplates.json";
import { getMissingBrowserTemplates } from "../utils/generatorRecovery";

const GENERATOR_SAVE_PREFS_KEY = "addanote.generatorSavePrefs.v1";
const CUSTOM_TEMPLATE_STORAGE_KEY = "addanote.customTemplates.v1";
const CUSTOM_TEMPLATE_GROUP_STORAGE_KEY = "addanote.customTemplateGroups.v1";
const TEMPLATE_ORDER_STORAGE_KEY = "addanote.generatorTemplateOrder.v1";
const HIDDEN_BUILT_IN_TEMPLATE_STORAGE_KEY = "addanote.hiddenBuiltInTemplates.v1";
const CUSTOM_FIELD_TYPES = ["text", "ipv4", "ipv4OrCidr", "vlan", "positiveInteger", "mask", "port"];
const GROUP_LABEL_SEPARATOR = " / ";

const GENERATOR_CATALOG = [
  {
    id: "nokia",
    label: "Nokia",
    detail: "Service, routing, and access configuration templates.",
    templateGroups: [
      {
        label: "Access",
        templates: [
          "Port Mirror 7210",
          "BOF configuration",
          "Access Port configuration",
          "Service Port Configuration",
        ],
      },
      {
        label: "Security",
        templates: [
          "CPM-Filter Configuration",
          "SNMP Configuration",
          "Login Control Configuration",
          "TACACS and Authentication Configuration",
        ],
      },
      {
        label: "Routing",
        templates: [
          "IS-IS L2 Configuration",
          "BGP Configuration",
          "VPRN with DHCP Configuration",
        ],
      },
      {
        label: "Services",
        templates: [
          "System Configuration",
          "Eth-CFM Service Configuration",
        ],
      },
      {
        label: "System Configurations",
        templates: [],
      },
    ],
  },
  {
    id: "juniper",
    label: "Juniper",
    detail: "Port mirror and ACX/MX/QFX template set.",
    templates: [
      "MX-Port Mirror",
      "QFX 5100 Port Mirror",
      "Juniper ACX Port Mirror Configuration",
    ],
  },
  {
    id: "adva-nid",
    label: "Adva NID",
    detail: "NID-specific Ethernet CFM template set.",
    templates: [
      "AdvaGE114Pro-Eth-CFM",
    ],
  },
];

function getVendorTemplates(vendor) {
  if (!vendor) return [];
  if (vendor.templateGroups) {
    return vendor.templateGroups.flatMap((group) => group.templates);
  }
  return vendor.templates || [];
}

function getVendorTemplateGroups(vendor) {
  if (!vendor) return [];
  if (vendor.templateGroups) return vendor.templateGroups;
  return [{ label: "Templates", templates: vendor.templates || [] }];
}

function splitGroupLabelPath(value) {
  const parts = String(value || "")
    .split(GROUP_LABEL_SEPARATOR)
    .map((part) => part.trim())
    .filter(Boolean);
  return {
    groupLabel: parts[0] || "Templates",
    subGroupLabel: parts.slice(1).join(GROUP_LABEL_SEPARATOR),
  };
}

function buildGroupLabelPath(groupLabel, subGroupLabel) {
  const topLevel = String(groupLabel || "").trim() || "Templates";
  const child = String(subGroupLabel || "").trim();
  return child ? `${topLevel}${GROUP_LABEL_SEPARATOR}${child}` : topLevel;
}

function getTopLevelGroupLabels(groupLabels) {
  return [...new Set(
    (Array.isArray(groupLabels) ? groupLabels : [])
      .map((label) => splitGroupLabelPath(label).groupLabel)
      .filter(Boolean),
  )];
}

function getChildGroupLabels(groupLabels, parentLabel) {
  const normalizedParent = String(parentLabel || "").trim();
  if (!normalizedParent) return [];
  return [...new Set(
    (Array.isArray(groupLabels) ? groupLabels : [])
      .map((label) => splitGroupLabelPath(label))
      .filter((entry) => entry.groupLabel === normalizedParent && entry.subGroupLabel)
      .map((entry) => entry.subGroupLabel),
  )];
}

function collectFolderPaths(entryList) {
  const folderSet = new Set();

  for (const entry of Array.isArray(entryList) ? entryList : []) {
    const rawPath = String(entry?.folder_path || "").trim();
    if (!rawPath) continue;

    const segments = rawPath.split("/").map((segment) => segment.trim()).filter(Boolean);
    let currentPath = "";

    for (const segment of segments) {
      currentPath = currentPath ? `${currentPath}/${segment}` : segment;
      folderSet.add(currentPath);
    }
  }

  return [...folderSet].sort((left, right) => left.localeCompare(right));
}

function createVendorIdFromLabel(label) {
  const normalized = String(label || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return normalized || "custom";
}

function createTemplateId(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const IPV4_PATTERN = "^(25[0-5]|2[0-4]\\d|1?\\d?\\d)(\\.(25[0-5]|2[0-4]\\d|1?\\d?\\d)){3}$";
const IPV4_OR_CIDR_PATTERN = "^((25[0-5]|2[0-4]\\d|1?\\d?\\d)(\\.(25[0-5]|2[0-4]\\d|1?\\d?\\d)){3})(\\/(3[0-2]|[12]?\\d))?$";
const VLAN_PATTERN = "^(409[0-4]|40[0-8]\\d|[1-3]?\\d{1,3})$";
const POSITIVE_INTEGER_PATTERN = "^[1-9]\\d*$";
const MASK_PATTERN = "^(3[0-2]|[12]?\\d)$";
const PORT_PATTERN = "^[A-Za-z0-9][A-Za-z0-9./:-]*$";

function getValidationProps(type, options = {}) {
  const { required = true } = options;
  const common = { required };
  switch (type) {
    case "ipv4":
      return { ...common, pattern: IPV4_PATTERN, inputMode: "decimal", title: "Enter a valid IPv4 address, for example 10.1.1.1" };
    case "ipv4OrCidr":
      return { ...common, pattern: IPV4_OR_CIDR_PATTERN, inputMode: "text", title: "Enter an IPv4 address or CIDR prefix, for example 10.1.1.0/24" };
    case "vlan":
      return { ...common, pattern: VLAN_PATTERN, inputMode: "numeric", title: "Enter a VLAN ID between 1 and 4094" };
    case "positiveInteger":
      return { ...common, pattern: POSITIVE_INTEGER_PATTERN, inputMode: "numeric", title: "Enter a positive whole number" };
    case "mask":
      return { ...common, pattern: MASK_PATTERN, inputMode: "numeric", title: "Enter a mask length between 0 and 32" };
    case "port":
      return { ...common, pattern: PORT_PATTERN, inputMode: "text", title: "Enter a valid interface or port identifier" };
    default:
      return common;
  }
}

function createValidatedSubmitHandler(handleGenerate) {
  return (event) => {
    event.preventDefault();
    if (!event.currentTarget.reportValidity()) return;
    handleGenerate();
  };
}

function applySampleForm(setForm, setGeneratedConfig, setCopyState, sampleForm) {
  setForm(sampleForm);
  setGeneratedConfig("");
  setCopyState("");
}

function downloadConfigAsText(filenameBase, content) {
  if (!content) return;
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filenameBase}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function escapeHtmlAttribute(value) {
  return escapeHtml(value).replace(/\r?\n/g, "&#10;");
}

function createGeneratedConfigId() {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `generated-config-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function readGeneratorSavePrefs() {
  if (typeof window === "undefined") return null;
  try {
    const rawValue = window.localStorage.getItem(GENERATOR_SAVE_PREFS_KEY);
    if (!rawValue) return null;
    const parsed = JSON.parse(rawValue);
    if (parsed && typeof parsed === "object" && ("global" in parsed || "templates" in parsed)) {
      return {
        global: parsed.global && typeof parsed.global === "object" ? parsed.global : {},
        templates: parsed.templates && typeof parsed.templates === "object" ? parsed.templates : {},
      };
    }
    return {
      global: parsed && typeof parsed === "object" ? parsed : {},
      templates: {},
    };
  } catch {
    return null;
  }
}

function writeGeneratorSavePrefs(value) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(GENERATOR_SAVE_PREFS_KEY, JSON.stringify(value));
  } catch {
    // Ignore storage write failures in private or restricted browsers.
  }
}

function readCustomTemplates() {
  if (typeof window === "undefined") return [];
  try {
    const rawValue = window.localStorage.getItem(CUSTOM_TEMPLATE_STORAGE_KEY);
    const parsed = rawValue ? JSON.parse(rawValue) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function readCustomTemplateGroups() {
  if (typeof window === "undefined") return [];
  try {
    const rawValue = window.localStorage.getItem(CUSTOM_TEMPLATE_GROUP_STORAGE_KEY);
    const parsed = rawValue ? JSON.parse(rawValue) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function clearCustomTemplateStorage() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CUSTOM_TEMPLATE_STORAGE_KEY);
    window.localStorage.removeItem(CUSTOM_TEMPLATE_GROUP_STORAGE_KEY);
  } catch {
    // Ignore storage write failures.
  }
}

function readTemplateOrderPrefs() {
  if (typeof window === "undefined") return {};
  try {
    const rawValue = window.localStorage.getItem(TEMPLATE_ORDER_STORAGE_KEY);
    const parsed = rawValue ? JSON.parse(rawValue) : {};
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function writeTemplateOrderPrefs(value) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(TEMPLATE_ORDER_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Ignore storage write failures.
  }
}

function readHiddenBuiltInTemplates() {
  if (typeof window === "undefined") return [];
  try {
    const rawValue = window.localStorage.getItem(HIDDEN_BUILT_IN_TEMPLATE_STORAGE_KEY);
    const parsed = rawValue ? JSON.parse(rawValue) : [];
    return Array.isArray(parsed) ? parsed.filter((value) => typeof value === "string") : [];
  } catch {
    return [];
  }
}

function writeHiddenBuiltInTemplates(value) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(HIDDEN_BUILT_IN_TEMPLATE_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Ignore storage write failures in private or restricted browsers.
  }
}

function createDefaultFieldDefinition(key) {
  const normalizedKey = String(key || "").trim();
  const lowerKey = normalizedKey.toLowerCase();
  const keyTokens = lowerKey.split(/[_-]+|(?=[A-Z])/).filter(Boolean);
  let type = "text";

  if (lowerKey.includes("vlan")) type = "vlan";
  else if (lowerKey.includes("port")) type = "port";
  else if (lowerKey.includes("mep") || lowerKey.includes("sdp")) type = "positiveInteger";
  else if (lowerKey.includes("mask")) type = "mask";
  else if (
    lowerKey === "ip"
    || lowerKey.endsWith("ip")
    || keyTokens.includes("ip")
    || lowerKey.includes("address")
    || lowerKey.includes("prefix")
  ) type = "ipv4OrCidr";

  return {
    key: normalizedKey,
    label: normalizedKey
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase()),
    type,
    placeholder: "",
    required: true,
  };
}

function parseQuickFieldSegment(segment) {
  const rawSegment = String(segment || "").trim();
  if (!rawSegment) return null;

  const separatorIndex = rawSegment.indexOf(":");
  const key = separatorIndex >= 0 ? rawSegment.slice(0, separatorIndex).trim() : rawSegment;
  const placeholder = separatorIndex >= 0 ? rawSegment.slice(separatorIndex + 1).trim() : "";

  if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(key)) {
    return null;
  }

  return {
    ...createDefaultFieldDefinition(key),
    placeholder,
  };
}

function normalizeCustomTemplateOutput(value) {
  return String(value || "").replace(/(^|[^{}])\{([A-Za-z][A-Za-z0-9_]*)\}(?!})/g, "$1{{$2}}");
}

function parseCustomTemplateFields(value) {
  const lines = String(value || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) {
    throw new Error("Add at least one field definition.");
  }

  if (lines.length === 1) {
    const segments = lines[0].split("|").map((part) => part.trim()).filter(Boolean);
    const quickFields = segments.map((segment) => parseQuickFieldSegment(segment)).filter(Boolean);
    const looksLikeSimpleFieldList = segments.length > 1 && quickFields.length === segments.length;
    if (looksLikeSimpleFieldList) {
      return quickFields;
    }
  }

  return lines.map((line, index) => {
    const [rawKey = "", rawLabel = "", rawType = "text", rawPlaceholder = "", rawRequired = "required"] = line.split("|").map((part) => part.trim());
    if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(rawKey)) {
      throw new Error(`Field ${index + 1} needs a valid key such as routerIp or customer_id.`);
    }
    if (!rawLabel) {
      throw new Error(`Field ${index + 1} needs a label.`);
    }
    if (!CUSTOM_FIELD_TYPES.includes(rawType)) {
      throw new Error(`Field ${index + 1} uses unsupported type "${rawType}".`);
    }
    const required = !["optional", "false", "no", "0"].includes(String(rawRequired || "").toLowerCase());
    return {
      key: rawKey,
      label: rawLabel,
      type: rawType,
      placeholder: rawPlaceholder,
      required,
    };
  });
}

function buildBuiltInTemplateKey(vendorId, templateName) {
  return `${String(vendorId || "").trim().toLowerCase()}::${String(templateName || "").trim().toLowerCase()}`;
}

function buildCatalogWithCustomTemplates(baseCatalog, customTemplates, customTemplateGroups, hiddenBuiltInTemplates = []) {
  const hiddenTemplateKeys = new Set(hiddenBuiltInTemplates);
  const vendorMap = new Map(
    (Array.isArray(baseCatalog) ? baseCatalog : []).map((vendor) => [
      vendor.id,
      {
        ...vendor,
        templates: vendor.templates
          ? vendor.templates.filter((template) => !hiddenTemplateKeys.has(buildBuiltInTemplateKey(vendor.id, template)))
          : undefined,
        templateGroups: vendor.templateGroups
          ? vendor.templateGroups.map((group) => ({
            ...group,
            templates: group.templates.filter((template) => !hiddenTemplateKeys.has(buildBuiltInTemplateKey(vendor.id, template))),
          }))
          : undefined,
      },
    ]),
  );

  for (const groupConfig of Array.isArray(customTemplateGroups) ? customTemplateGroups : []) {
    const vendorId = groupConfig.vendorId || createVendorIdFromLabel(groupConfig.vendorLabel);
    const vendorLabel = groupConfig.vendorLabel || "Custom";
    const groupLabel = String(groupConfig.label || "").trim();
    if (!groupLabel) continue;
    let vendor = vendorMap.get(vendorId);

    if (!vendor) {
      vendor = {
        id: vendorId,
        label: vendorLabel,
        detail: "Admin-created templates.",
        templateGroups: [],
      };
      vendorMap.set(vendorId, vendor);
    }

    if (!vendor.templateGroups) {
      vendor.templateGroups = [{ label: "Templates", templates: vendor.templates ? [...vendor.templates] : [] }];
      delete vendor.templates;
    }

    if (!vendor.templateGroups.some((entry) => entry.label === groupLabel)) {
      vendor.templateGroups.push({ label: groupLabel, templates: [] });
    }
  }

  for (const template of Array.isArray(customTemplates) ? customTemplates : []) {
    const vendorId = template.vendorId || createVendorIdFromLabel(template.vendorLabel);
    const vendorLabel = template.vendorLabel || "Custom";
    const groupLabel = template.groupLabel || "Templates";
    let vendor = vendorMap.get(vendorId);

    if (!vendor) {
      vendor = {
        id: vendorId,
        label: vendorLabel,
        detail: "Admin-created templates.",
        templateGroups: [],
      };
      vendorMap.set(vendorId, vendor);
    }

    if (!vendor.templateGroups) {
      vendor.templateGroups = [{ label: "Templates", templates: vendor.templates ? [...vendor.templates] : [] }];
      delete vendor.templates;
    }

    let group = vendor.templateGroups.find((entry) => entry.label === groupLabel);
    if (!group) {
      group = { label: groupLabel, templates: [] };
      vendor.templateGroups.push(group);
    }
    if (!group.templates.includes(template.name)) {
      group.templates.push(template.name);
    }
  }

  return [...vendorMap.values()];
}

function buildGeneratorTemplateKey(vendorLabel, templateTitle) {
  return `${String(vendorLabel || "generator").trim().toLowerCase()}::${String(templateTitle || "template").trim().toLowerCase()}`;
}

function buildTemplateOrderKey(vendorId, groupLabel) {
  return `${String(vendorId || "").trim().toLowerCase()}::${String(groupLabel || "Templates").trim().toLowerCase()}`;
}

function buildDraggableTemplateKey(vendorId, groupLabel, templateName) {
  return `${buildTemplateOrderKey(vendorId, groupLabel)}::${String(templateName || "").trim().toLowerCase()}`;
}

function parseDraggableTemplateKey(value) {
  const parts = String(value || "").split("::");
  if (parts.length < 3) return null;
  return {
    vendorId: parts[0] || "",
    groupLabel: parts[1] || "Templates",
    templateName: parts.slice(2).join("::"),
  };
}

function buildTemplateGroupDropKey(vendorId, groupLabel) {
  return `group::${buildTemplateOrderKey(vendorId, groupLabel)}`;
}

function isCustomTemplateGroup(customTemplateGroups, vendorId, groupLabel) {
  return (Array.isArray(customTemplateGroups) ? customTemplateGroups : []).some((group) => (
    (group.vendorId || createVendorIdFromLabel(group.vendorLabel)) === vendorId
    && String(group.label || "").trim() === String(groupLabel || "").trim()
  ));
}

function applyTemplateOrder(groups, vendorId, orderPrefs) {
  const nextGroups = (Array.isArray(groups) ? groups : []).map((group) => ({
    ...group,
    templates: Array.isArray(group?.templates) ? [...group.templates] : [],
  }));
  const vendorPrefix = `${String(vendorId || "").trim().toLowerCase()}::`;
  const groupMap = new Map(
    nextGroups.map((group) => [String(group?.label || "").trim().toLowerCase(), group]),
  );
  const templateLocationMap = new Map();

  for (const group of nextGroups) {
    for (const template of group.templates) {
      templateLocationMap.set(String(template), group.label);
    }
  }

  for (const [key, savedOrder] of Object.entries(orderPrefs || {})) {
    if (!key.startsWith(vendorPrefix) || !Array.isArray(savedOrder)) continue;
    const groupLabel = key.slice(vendorPrefix.length) || "Templates";
    const targetGroup = groupMap.get(groupLabel);
    if (!targetGroup) continue;

    for (const template of savedOrder) {
      const currentGroupLabel = templateLocationMap.get(String(template));
      if (!currentGroupLabel || currentGroupLabel === groupLabel) continue;
      const currentGroup = groupMap.get(String(currentGroupLabel).trim().toLowerCase());
      if (!currentGroup) continue;
      currentGroup.templates = currentGroup.templates.filter((entry) => entry !== template);
      if (!targetGroup.templates.includes(template)) {
        targetGroup.templates.push(template);
      }
      templateLocationMap.set(String(template), targetGroup.label);
    }
  }

  return nextGroups.map((group) => {
    const savedOrder = orderPrefs?.[buildTemplateOrderKey(vendorId, group?.label)] || [];
    if (!Array.isArray(savedOrder) || !savedOrder.length) {
      return group;
    }

    const ordered = savedOrder.filter((template) => group.templates.includes(template));
    const remainder = group.templates.filter((template) => !ordered.includes(template));
    return { ...group, templates: [...ordered, ...remainder] };
  });
}

function getCustomTemplateVendorId(template) {
  return template?.vendorId || createVendorIdFromLabel(template?.vendorLabel);
}

function serializeCustomTemplateFields(fields) {
  return (Array.isArray(fields) ? fields : [])
    .map((field) => [
      field?.key || "",
      field?.label || "",
      field?.type || "text",
      field?.placeholder || "",
      field?.required === false ? "optional" : "required",
    ].join(" | "))
    .join("\n");
}

function formatSavedTimestamp(value) {
  const date = value instanceof Date ? value : new Date(value || Date.now());
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function buildGeneratedNoteMarkup({ draft, userLabel, savedAt }) {
  const timestampLabel = formatSavedTimestamp(savedAt);
  const generatedConfig = String(draft?.generatedConfig || "");
  const generatedConfigId = createGeneratedConfigId();
  return `
<section class="generated-config-note" data-generator-note="true" data-generated-config-id="${escapeHtmlAttribute(generatedConfigId)}">
  <div class="generated-config-meta">
    <p><strong>Generator:</strong> ${escapeHtml(draft?.templateTitle || "Generated Config")}</p>
    <p><strong>Vendor:</strong> ${escapeHtml(draft?.vendorLabel || "Generator")}</p>
    <p><strong>Saved:</strong> ${escapeHtml(timestampLabel)}</p>
    <p><strong>User:</strong> ${escapeHtml(userLabel || "Unknown user")}</p>
  </div>
  <div class="generated-config-toolbar">
    <button
      type="button"
      class="generated-config-copy-button"
      data-copy-generated-config="true"
      data-generated-config-id="${escapeHtmlAttribute(generatedConfigId)}"
      data-generated-config="${escapeHtmlAttribute(generatedConfig)}"
    >Copy config</button>
  </div>
  <pre data-generated-config-text="true"><code>${escapeHtml(generatedConfig)}</code></pre>
</section>
`.trim();
}

function isEditableShortcutTarget(target) {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = String(target.tagName || "").toLowerCase();
  return tagName === "input" || tagName === "textarea" || tagName === "select" || target.isContentEditable;
}

function clickElement(selector) {
  const element = typeof document !== "undefined" ? document.querySelector(selector) : null;
  if (!(element instanceof HTMLButtonElement) || element.disabled) return false;
  element.click();
  return true;
}

function GeneratorOutputHeader({
  title,
  generatedConfig,
  copyState,
  onCopy,
  onClose,
  onDownload,
  onSaveToNote,
}) {
  return (
    <div className="config-generator-output-header">
      <div>
        <span className="eyebrow">Generated Output</span>
        <strong>{title}</strong>
      </div>
      <div className="config-generator-output-controls">
        <button
          type="button"
          className="action-button action-button-secondary"
          data-shortcut="save-output"
          onClick={onSaveToNote}
          disabled={!generatedConfig}
        >
          Save to note
        </button>
        <button
          type="button"
          className="action-button action-button-secondary"
          onClick={onDownload}
          disabled={!generatedConfig}
        >
          Download .txt
        </button>
        <button
          type="button"
          className="action-button action-button-secondary"
          data-shortcut="copy-output"
          onClick={onCopy}
          disabled={!generatedConfig}
        >
          {copyState || "Copy output"}
        </button>
        <button
          type="button"
          className="action-button action-button-secondary"
          data-shortcut="copy-close"
          onClick={async () => {
            await onCopy?.();
            onClose?.();
          }}
          disabled={!generatedConfig}
        >
          Copy + close
        </button>
      </div>
    </div>
  );
}

function CustomTemplateDialog({
  open,
  form,
  setForm,
  availableGroupLabels = [],
  errorMessage,
  onClose,
  onSubmit,
  mode = "create",
}) {
  if (!open) return null;

  const isEditMode = mode === "edit" || mode === "edit-built-in";

  const topLevelGroupLabels = getTopLevelGroupLabels(availableGroupLabels);
  const availableSubGroupLabels = getChildGroupLabels(availableGroupLabels, form.groupLabel);

  return (
    <div className="generator-save-overlay" role="dialog" aria-modal="true" aria-labelledby="custom-template-title">
      <div className="generator-save-dialog">
        <div className="section-heading section-heading-compact">
          <div>
            <span className="eyebrow">Admin Template</span>
            <h3 id="custom-template-title">{isEditMode ? "Edit Template" : "Create New Template"}</h3>
          </div>
          <button type="button" className="action-button action-button-secondary" onClick={onClose}>
            Close
          </button>
        </div>

        <p className="config-generator-intro">
          {isEditMode
            ? <>Update the template fields and output. Use placeholders like <code>{'{{routerIp}}'}</code> inside the output template.</>
            : <>Create a reusable generator template. Use placeholders like <code>{'{{routerIp}}'}</code> inside the output template.</>}
        </p>

        <form
          className="config-generator-form"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
        >
          <label className="editor-field">
            <span>Template Name</span>
            <input
              value={form.name}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              placeholder="Example: Static Route Builder"
            />
          </label>

          <div className="editor-split-fields">
            <label className="editor-field">
              <span>Vendor Label</span>
              <input
                value={form.vendorLabel}
                onChange={(event) => setForm((current) => ({ ...current, vendorLabel: event.target.value }))}
                placeholder="Example: Nokia"
              />
            </label>
            <label className="editor-field">
              <span>Section</span>
              <input
                list="generator-group-labels"
                value={form.groupLabel}
                onChange={(event) => setForm((current) => ({ ...current, groupLabel: event.target.value }))}
                placeholder="Example: Routing"
              />
            </label>
          </div>
          <datalist id="generator-group-labels">
            {topLevelGroupLabels.map((label) => (
              <option key={label} value={label} />
            ))}
          </datalist>

          <label className="editor-field">
            <span>Subsection</span>
            <input
              list="generator-subgroup-labels"
              value={form.subGroupLabel || ""}
              onChange={(event) => setForm((current) => ({ ...current, subGroupLabel: event.target.value }))}
              placeholder="Optional: Core"
            />
            <small>Optional. Example: choose <code>Routing</code> and enter <code>Core</code> to place the template under <code>Routing / Core</code>.</small>
          </label>
          <datalist id="generator-subgroup-labels">
            {availableSubGroupLabels.map((label) => (
              <option key={label} value={label} />
            ))}
          </datalist>

          <label className="editor-field">
            <span>Short Description</span>
            <input
              value={form.detail}
              onChange={(event) => setForm((current) => ({ ...current, detail: event.target.value }))}
              placeholder="What this template generates"
            />
          </label>

          <label className="editor-field">
            <span>Field Definitions</span>
            <textarea
              rows={7}
              value={form.fieldSchema}
              onChange={(event) => setForm((current) => ({ ...current, fieldSchema: event.target.value }))}
              placeholder={"routerIp | Router IP | ipv4 | 10.1.1.1 | required\npeerAsn | Peer ASN | positiveInteger | 65010 | required"}
            />
            <small>Use one field per line: key | label | type | placeholder | required/optional. Quick mode also works: epipe | vlan | customer | port or epipe:1001 | vlan:2000</small>
          </label>

          <label className="editor-field">
            <span>Output Template</span>
            <textarea
              rows={10}
              value={form.outputTemplate}
              onChange={(event) => setForm((current) => ({ ...current, outputTemplate: event.target.value }))}
              placeholder={"configure router static-route {{prefix}} next-hop {{nextHop}}"}
            />
            <small>Use placeholders like <code>{'{{fieldKey}}'}</code>. Single braces like <code>{'{fieldKey}'}</code> are accepted too.</small>
          </label>

          {errorMessage ? <div className="feedback-panel error-message">{errorMessage}</div> : null}

          <div className="config-generator-actions">
            <button type="submit" className="action-button">{isEditMode ? "Update template" : "Save template"}</button>
            <button type="button" className="action-button action-button-secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateTemplateGroupDialog({
  open,
  form,
  setForm,
  errorMessage,
  onClose,
  onSubmit,
}) {
  if (!open) return null;

  return (
    <div className="generator-save-overlay" role="dialog" aria-modal="true" aria-labelledby="custom-template-group-title">
      <div className="generator-save-dialog">
        <div className="section-heading section-heading-compact">
          <div>
            <span className="eyebrow">Vendor Section</span>
            <h3 id="custom-template-group-title">Create Template Section</h3>
          </div>
          <button type="button" className="action-button action-button-secondary" onClick={onClose}>
            Close
          </button>
        </div>

        <p className="config-generator-intro">
          Create a new section heading for this vendor, such as <code>Network</code>, then add templates into it.
        </p>

        <form
          className="config-generator-form"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
        >
          <div className="editor-split-fields">
            <label className="editor-field">
              <span>Vendor Label</span>
              <input
                value={form.vendorLabel}
                onChange={(event) => setForm((current) => ({ ...current, vendorLabel: event.target.value }))}
                placeholder="Example: Nokia"
              />
            </label>
            <label className="editor-field">
              <span>Section Label</span>
              <input
                value={form.label}
                onChange={(event) => setForm((current) => ({ ...current, label: event.target.value }))}
                placeholder="Example: Network"
              />
            </label>
          </div>

          {errorMessage ? <div className="feedback-panel error-message">{errorMessage}</div> : null}

          <div className="config-generator-actions">
            <button type="submit" className="action-button">Create section</button>
            <button type="button" className="action-button action-button-secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteConfirmationDialog({ open, itemLabel, itemType, onCancel, onConfirm }) {
  if (!open) return null;

  return (
    <div className="generator-save-overlay" role="dialog" aria-modal="true" aria-labelledby="delete-confirmation-title">
      <div className="generator-save-dialog delete-confirmation-dialog">
        <div className="section-heading section-heading-compact">
          <div>
            <span className="eyebrow">Confirm deletion</span>
            <h3 id="delete-confirmation-title">Delete {itemType}?</h3>
          </div>
        </div>
        <p className="config-generator-intro">
          Delete <strong>{itemLabel}</strong>? This action cannot be undone.
        </p>
        <div className="config-generator-actions">
          <button type="button" className="action-button danger" onClick={onConfirm}>Delete</button>
          <button type="button" className="action-button action-button-secondary" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function CustomTemplateGenerator({ template, onClose, onSaveRequest }) {
  const [form, setForm] = useState(
    Object.fromEntries((template?.fields || []).map((field) => [field.key, ""])),
  );
  const [generatedConfig, setGeneratedConfig] = useState("");
  const [copyState, setCopyState] = useState("");
  const hasAllRequiredFields = useMemo(
    () => (template?.fields || []).every((field) => !field.required || String(form[field.key] || "").trim()),
    [form, template],
  );

  useEffect(() => {
    setForm(Object.fromEntries((template?.fields || []).map((field) => [field.key, ""])));
    setGeneratedConfig("");
    setCopyState("");
  }, [template]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setCopyState("");
  };

  const handleGenerate = () => {
    const output = normalizeCustomTemplateOutput(template?.outputTemplate || "").replace(/{{\s*([A-Za-z0-9_]+)\s*}}/g, (_match, key) => String(form[key] || ""));
    setGeneratedConfig(output.trim());
    setCopyState("");
  };

  const handleClear = () => {
    setForm(Object.fromEntries((template?.fields || []).map((field) => [field.key, ""])));
    setGeneratedConfig("");
    setCopyState("");
  };

  const handleCopy = async () => {
    if (!generatedConfig) return;
    try {
      await navigator.clipboard.writeText(generatedConfig);
      setCopyState("Copied");
    } catch {
      setCopyState("Copy failed");
    }
  };

  const handleDownload = () => {
    downloadConfigAsText(createTemplateId(template?.name || "custom-template"), generatedConfig);
  };

  return (
    <div className="generator-template-workbench">
      <form className="config-generator-form" onSubmit={createValidatedSubmitHandler(handleGenerate)}>
        {(template?.fields || []).map((field) => (
          <label className="editor-field" key={field.key}>
            <span>{field.label}</span>
            <input
              name={field.key}
              value={form[field.key] || ""}
              onChange={handleChange}
              placeholder={field.placeholder || ""}
              {...getValidationProps(field.type, { required: field.required })}
            />
          </label>
        ))}
        <div className="config-generator-actions">
          <button type="submit" className="action-button" disabled={!hasAllRequiredFields}>Generate configuration</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleClear}>Clear form</button>
        </div>
      </form>

      <div className="config-generator-output-shell">
        <GeneratorOutputHeader
          title={template?.name || "Custom Template"}
          generatedConfig={generatedConfig}
          copyState={copyState}
          onCopy={handleCopy}
          onClose={onClose}
          onDownload={handleDownload}
          onSaveToNote={() => onSaveRequest?.({ templateTitle: template?.name || "Custom Template", generatedConfig, vendorLabel: template?.vendorLabel || "Custom" })}
        />
        <pre className="config-generator-output">{generatedConfig || "Complete the fields and generate the configuration output here."}</pre>
      </div>
    </div>
  );
}

function SaveGeneratorNoteDialog({
  open,
  draft,
  titles,
  filteredTitles,
  folderOptions,
  loadingTitles,
  loadingTargetEntries,
  targetEntries,
  titleQuery,
  setTitleQuery,
  saveMode,
  setSaveMode,
  form,
  setForm,
  saving,
  errorMessage,
  successMessage,
  onClose,
  onSubmit,
}) {
  if (!open || !draft) return null;

  return (
    <div className="generator-save-overlay" role="dialog" aria-modal="true" aria-labelledby="generator-save-title">
      <div className="generator-save-dialog">
        <div className="section-heading section-heading-compact">
          <div>
            <span className="eyebrow">Admin Save</span>
            <h3 id="generator-save-title">Save Generated Config</h3>
          </div>
          <button type="button" className="action-button action-button-secondary" onClick={onClose}>
            Close
          </button>
        </div>

        <p className="config-generator-intro">
          Save <strong>{draft.templateTitle}</strong> into an existing title or create a new title with this output as the first note.
        </p>

        <form
          className="config-generator-form"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
        >
          <div className="generator-save-mode-row">
            <button
              type="button"
              className={`action-button ${saveMode === "existing" ? "" : "action-button-secondary"}`}
              onClick={() => setSaveMode("existing")}
            >
              Existing title
            </button>
            <button
              type="button"
              className={`action-button ${saveMode === "new" ? "" : "action-button-secondary"}`}
              onClick={() => setSaveMode("new")}
            >
              New title
            </button>
          </div>

          {saveMode === "existing" ? (
            <>
              <label className="editor-field">
                <span>Search Titles</span>
                <input
                  value={titleQuery}
                  onChange={(event) => setTitleQuery(event.target.value)}
                  placeholder="Filter titles by name"
                  disabled={loadingTitles || saving}
                />
              </label>
              <label className="editor-field">
                <span>Choose Title</span>
                <select
                  value={form.groupId}
                  onChange={(event) => setForm((current) => ({ ...current, groupId: event.target.value, entryId: "" }))}
                  disabled={loadingTitles || saving}
                  required
                >
                  <option value="">Select a title</option>
                  {filteredTitles.map((title) => (
                    <option key={title.id} value={title.id}>
                      {title.title || "Untitled"}
                    </option>
                  ))}
                </select>
                <small>
                  {loadingTitles
                    ? "Loading titles..."
                    : `${filteredTitles.length} of ${titles.length} title${titles.length === 1 ? "" : "s"} shown`}
                </small>
              </label>
              <div className="generator-save-mode-row">
                <button
                  type="button"
                  className={`action-button ${form.existingAction === "new-entry" ? "" : "action-button-secondary"}`}
                  onClick={() => setForm((current) => ({ ...current, existingAction: "new-entry", entryId: "" }))}
                >
                  New child note
                </button>
                <button
                  type="button"
                  className={`action-button ${form.existingAction === "append-entry" ? "" : "action-button-secondary"}`}
                  onClick={() => setForm((current) => ({ ...current, existingAction: "append-entry" }))}
                  disabled={!form.groupId}
                >
                  Append to note
                </button>
              </div>
              {form.existingAction === "append-entry" ? (
                <label className="editor-field">
                  <span>Choose Note</span>
                  <select
                    value={form.entryId}
                    onChange={(event) => setForm((current) => ({ ...current, entryId: event.target.value }))}
                    disabled={!form.groupId || loadingTargetEntries || saving}
                    required
                  >
                    <option value="">Select a note</option>
                    {targetEntries.map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {entry.title || "Untitled"}{entry.folder_path ? ` - ${entry.folder_path}` : ""}
                      </option>
                    ))}
                  </select>
                  <small>
                    {loadingTargetEntries
                      ? "Loading notes in this title..."
                      : `${targetEntries.length} note${targetEntries.length === 1 ? "" : "s"} available to append`}
                  </small>
                </label>
              ) : null}
            </>
          ) : (
            <label className="editor-field">
              <span>New Title Name</span>
              <input
                value={form.groupTitle}
                onChange={(event) => setForm((current) => ({ ...current, groupTitle: event.target.value }))}
                placeholder="Generator Configurations"
                maxLength={200}
                required
              />
            </label>
          )}

          {!(saveMode === "existing" && form.existingAction === "append-entry") ? (
            <label className="editor-field">
              <span>Note Title</span>
              <input
                value={form.entryTitle}
                onChange={(event) => setForm((current) => ({ ...current, entryTitle: event.target.value }))}
                placeholder={`${draft.templateTitle} output`}
                maxLength={200}
                required
              />
            </label>
          ) : null}

          {!(saveMode === "existing" && form.existingAction === "append-entry") ? (
            <label className="editor-field">
              <span>Folder</span>
              <select
                value={form.folderPath}
                onChange={(event) => setForm((current) => ({ ...current, folderPath: event.target.value }))}
                disabled={(saveMode === "existing" && !form.groupId) || saving}
              >
                <option value="">Top level</option>
                {folderOptions.map((folderPath) => (
                  <option key={folderPath} value={folderPath}>
                    {folderPath}
                  </option>
                ))}
              </select>
              <small>
                {saveMode === "existing"
                  ? (folderOptions.length
                    ? "Choose an existing folder inside this title."
                    : "No folders found in this title yet. The note will save at the top level.")
                  : "New titles start at the top level. Create folders after the title exists."}
              </small>
            </label>
          ) : null}

          <label className="editor-field">
            <span>Tags</span>
            <input
              value={form.tags}
              onChange={(event) => setForm((current) => ({ ...current, tags: event.target.value }))}
              placeholder="generator, nokia, bgp"
              maxLength={300}
            />
          </label>

          {errorMessage ? <div className="feedback-panel error-message">{errorMessage}</div> : null}
          {successMessage ? <div className="feedback-panel success-message">{successMessage}</div> : null}

          <div className="config-generator-actions">
            <button type="submit" className="action-button" data-shortcut="save-note" disabled={saving}>
              {saving ? "Saving..." : saveMode === "existing" && form.existingAction === "append-entry" ? "Append to note" : "Save to note"}
            </button>
            <button type="button" className="action-button action-button-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const PORT_MIRROR_7210_INITIAL_FORM = {
  portId1: "",
  portId2: "",
  sid: "",
};
const PORT_MIRROR_7210_SAMPLE_FORM = {
  portId1: "1/1/c1/1",
  portId2: "1/1/c2/1",
  sid: "101",
};

const BOF_INITIAL_FORM = {
  imageFile: "",
  configFile: "",
  dnsServerIp: "",
  dnsName: "",
};
const BOF_SAMPLE_FORM = {
  imageFile: "cf3:\\images\\TiMOS-SR-20.10.R13",
  configFile: "cf3:\\LabRouter01.cfg",
  dnsServerIp: "10.1.1.20",
  dnsName: "lab.charter.net",
};

const ACCESS_PORT_INITIAL_FORM = {
  cfp: "",
  description: "",
  mtu: "",
};
const ACCESS_PORT_SAMPLE_FORM = {
  cfp: "1/1/c1/1",
  description: "Customer handoff",
  mtu: "9212",
};

const SERVICE_PORT_INITIAL_FORM = {
  cfp: "",
  portDescription: "",
};
const SERVICE_PORT_SAMPLE_FORM = {
  cfp: "1/1/c1/1",
  portDescription: "Managed service port",
};

const BGP_INITIAL_FORM = {
  authenticationKey: "",
  err1Loopback: "",
  err1Name: "",
  systemIp: "",
  err2Loopback: "",
  err2Name: "",
  localAddress: "",
};
const BGP_SAMPLE_FORM = {
  authenticationKey: "Charter123!",
  err1Loopback: "10.10.10.1",
  err1Name: "ERR1",
  systemIp: "10.10.10.9",
  err2Loopback: "10.10.10.2",
  err2Name: "ERR2",
  localAddress: "10.10.10.9",
};

const SNMP_INITIAL_FORM = {
  readOnlyCommunity: "",
  readWriteCommunity: "",
  readWriteAllCommunity: "",
};
const SNMP_SAMPLE_FORM = {
  readOnlyCommunity: "public-ro",
  readWriteCommunity: "public-rw",
  readWriteAllCommunity: "public-rwa",
};

const SYSTEM_INITIAL_FORM = {
  routerHostname: "",
  charterContact: "",
  routerLocation: "",
  clliCodeInfo: "",
  coordinates: "",
  primaryNtpServerIp: "",
  secondaryNtpServerIp: "",
  standardZoneName: "",
};
const SYSTEM_SAMPLE_FORM = {
  routerHostname: "LAB-7210-01",
  charterContact: "NOC",
  routerLocation: "Charlotte, NC",
  clliCodeInfo: "CLTNC001",
  coordinates: "35.2271,-80.8431",
  primaryNtpServerIp: "10.1.1.20",
  secondaryNtpServerIp: "10.1.1.21",
  standardZoneName: "EST5EDT",
};

const LOGIN_CONTROL_INITIAL_FORM = {
  inboundSessionsAllowed: "",
  idleTime: "",
  preLoginMessage: "",
  motd: "",
};
const LOGIN_CONTROL_SAMPLE_FORM = {
  inboundSessionsAllowed: "5",
  idleTime: "15",
  preLoginMessage: "Authorized access only",
  motd: "Welcome to AddaNote Lab Router",
};

const TACACS_INITIAL_FORM = {
  numberOfAttempts: "",
  numberOfTries: "",
  lockoutDuration: "",
  primaryTacacsIp: "",
  primaryTacacsPassword: "",
  secondaryTacacsIp: "",
  secondaryTacacsPassword: "",
};
const TACACS_SAMPLE_FORM = {
  numberOfAttempts: "3",
  numberOfTries: "5",
  lockoutDuration: "10",
  primaryTacacsIp: "10.20.30.40",
  primaryTacacsPassword: "TacacsPrimary!",
  secondaryTacacsIp: "10.20.30.41",
  secondaryTacacsPassword: "TacacsSecondary!",
};

const CPM_FILTER_INITIAL_FORM = {
  entry1: "",
  descriptionOfEntry1: "",
  protocolType: "",
  ipPrefixMask1: "",
  entry2: "",
  descriptionOfEntry2: "",
  ipPrefixMask2: "",
};
const CPM_FILTER_SAMPLE_FORM = {
  entry1: "10",
  descriptionOfEntry1: "Permit NOC subnet",
  protocolType: "tcp",
  ipPrefixMask1: "10.0.0.0/8",
  entry2: "20",
  descriptionOfEntry2: "Permit management host",
  ipPrefixMask2: "192.168.1.10",
};

const ISIS_L2_INITIAL_FORM = {
  areaId: "",
  interfaceToEdr: "",
  password: "",
};
const ISIS_L2_SAMPLE_FORM = {
  areaId: "49.0001",
  interfaceToEdr: "to-ERR-1",
  password: "ISISKey123",
};

const ETH_CFM_SERVICE_INITIAL_FORM = {
  domain: "",
  association: "",
  adminName: "",
  remoteMep: "",
  customer: "",
  epipe: "",
  port: "",
  mep: "",
  vlan: "",
  sdp: "",
  stringName: "",
  bridgeId: "",
};
const ETH_CFM_SERVICE_SAMPLE_FORM = {
  domain: "100",
  association: "200",
  adminName: "SERVICE-200",
  remoteMep: "301",
  customer: "50",
  epipe: "2001",
  port: "1/1/c1/1",
  mep: "201",
  vlan: "300",
  sdp: "5001",
  stringName: "SERVICE-200",
  bridgeId: "300",
};

const VPRN_DHCP_INITIAL_FORM = {
  customerId: "",
  nameForUse: "",
  serviceName: "",
  cmlName: "",
  localAsn: "",
  rdForCml: "",
  rtForCml: "",
  vrfName: "",
  ipv4Address: "",
  mask: "",
  dhcpServerAddress: "",
  sapPort: "",
};
const VPRN_DHCP_SAMPLE_FORM = {
  customerId: "50",
  nameForUse: "Lab-Customer",
  serviceName: "Customer VPRN",
  cmlName: "CML-LAB",
  localAsn: "65010",
  rdForCml: "65010:99",
  rtForCml: "target:65010:99",
  vrfName: "cust-vrf-1",
  ipv4Address: "10.1.1.1",
  mask: "29",
  dhcpServerAddress: "10.1.1.2",
  sapPort: "1/1/c1/1",
};

const JUNIPER_PORT_MIRROR_INITIAL_FORM = {
  mirrorPort: "",
  mirrorVlan: "",
  filterPort: "",
  filterVlan: "",
};
const JUNIPER_PORT_MIRROR_SAMPLE_FORM = {
  mirrorPort: "xe-0/0/49",
  mirrorVlan: "1401",
  filterPort: "ge-0/0/9",
  filterVlan: "151",
};

const ADVA_ETH_CFM_INITIAL_FORM = {
  mdLevel: "",
  svlan: "",
  cvlan: "",
  svlanPcp: "",
  mepNumber: "",
  remoteMepNumber: "",
  mdString: "",
  manetId: "",
  manetName: "",
  cfp: "",
  flowNumber: "",
};
const ADVA_ETH_CFM_SAMPLE_FORM = {
  mdLevel: "4",
  svlan: "1200",
  cvlan: "2200",
  svlanPcp: "5",
  mepNumber: "1001",
  remoteMepNumber: "1002",
  mdString: "CPE-MD",
  manetId: "200",
  manetName: "MA-CUST-200",
  cfp: "3",
  flowNumber: "1",
};

function buildPortMirror7210Config({ portId1, portId2, sid }) {
  return `
configure port ${portId1}
shutdown
ethernet
mode access
encap-type null
exit
no shutdown
exit all


configure mirror mirror-dest ${sid} create
sap ${portId1} create
exit
no shutdown
exit all

debug mirror-source ${sid}
port ${portId2} egress ingress
exit all
`.trim();
}

function buildBofConfig({
  imageFile,
  configFile,
  dnsServerIp,
  dnsName,
}) {
  return `
bof
primary-image ${imageFile}
primary-config ${configFile}
primary-dns ${dnsServerIp}
dns-domain ${dnsName}
persist on
exit
bof save


Verification:

show bof

Save Bof:

bof save

Verision Check:
version check boot.tim and both.tim


Ref: https://chalk.charter.com/x/IQskVw
`.trim();
}

function buildAccessPortConfig({
  cfp,
  description,
  mtu,
}) {
  return `
configure port ${cfp}
description ${description}
ethernet
mode access
access
exit
encap-type dot1q
mtu ${mtu}
exit all
`.trim();
}

function buildServicePortConfig({
  cfp,
  portDescription,
}) {
  return `
configure
port ${cfp}
    description "${portDescription}"
    ethernet
        mode access
        access
        exit
        encap-type dot1q
        mtu 9212
    exit
    no shutdown
exit
`.trim();
}

function buildBgpConfig({
  authenticationKey,
  err1Loopback,
  err1Name,
  systemIp,
  err2Loopback,
  err2Name,
  localAddress,
}) {
  return `
configure router
    bgp
        #Configure BGP labeled routes next hop resolution using RSVP
        next-hop-resolution
            labeled-routes
                transport-tunnel
                    family label-ipv4
                        resolution-filter
                            ldp
                        exit
                        resolution any
                    exit
                exit
            exit
        exit

        # BGP group configuration towards route reflectors
        group "BG_SE_IBGP-CBH_4"
            family ipv4 vpn-ipv4 label-ipv4
            authentication-key ${authenticationKey}
            type internal
            import "BP_LABEL-LOOPBACK_I_4"
            export "BP_LABEL-LOOPBACK_O_4"
            neighbor ${err1Loopback}
                description ${err1Name}
                local-address ${systemIp}
                next-hop-self
                add-paths
                    label-ipv4 send none receive
                exit
            exit
            neighbor ${err2Loopback}
                description ${err2Name}
                local-address ${localAddress}
                next-hop-self
                add-paths
                    label-ipv4 send none receive
                exit
            exit
        exit
        no shutdown
`.trim();
}

function buildSnmpConfig({
  readOnlyCommunity,
  readWriteCommunity,
  readWriteAllCommunity,
}) {
  return `
configure system security
        snmp
            community "${readOnlyCommunity}" r version both
            community "${readWriteCommunity}" rw version both
            community "${readWriteAllCommunity}" rwa version both
        exit
    exit
`.trim();
}

function buildSystemConfig({
  routerHostname,
  charterContact,
  routerLocation,
  clliCodeInfo,
  coordinates,
  primaryNtpServerIp,
  secondaryNtpServerIp,
  standardZoneName,
}) {
  return `
configure system
    name "${routerHostname}"
    contact "${charterContact}"
    location "${routerLocation}"
    clli-code "${clliCodeInfo}"
    coordinates "${coordinates}"
    snmp
        packet-size 9216
    exit
    time
        ntp
            server ${primaryNtpServerIp} prefer
            server ${secondaryNtpServerIp}
            no shutdown
        exit
        sntp
            shutdown
        exit
        dst-zone ${standardZoneName}
            start second sunday march 02:00
            end first sunday november 02:00
        exit
        zone ${standardZoneName}
    exit
exit
`.trim();
}

function buildLoginControlConfig({
  inboundSessionsAllowed,
  idleTime,
  preLoginMessage,
  motd,
}) {
  return `
configure system
    login-control
        ssh
            inbound-max-sessions ${inboundSessionsAllowed}
        exit
        idle-timeout ${idleTime}
        pre-login-message "${preLoginMessage}"
        motd text "${motd}"
    exit
exit
`.trim();
}

function buildTacacsConfig({
  numberOfAttempts,
  numberOfTries,
  lockoutDuration,
  primaryTacacsIp,
  primaryTacacsPassword,
  secondaryTacacsIp,
  secondaryTacacsPassword,
}) {
  return `
configure system security
        password
            authentication-order tacplus local exit-on-reject
            attempts ${numberOfAttempts} time ${numberOfTries} lockout ${lockoutDuration}
        exit
        tacplus
            accounting record-type start-stop
            authorization
            server 1 address ${primaryTacacsIp} secret "${primaryTacacsPassword}"
            server 2 address ${secondaryTacacsIp} secret "${secondaryTacacsPassword}"
        exit
        ssh
            preserve-key
        exit
exit
`.trim();
}

function buildCpmFilterConfig({
  entry1,
  descriptionOfEntry1,
  protocolType,
  ipPrefixMask1,
  entry2,
  descriptionOfEntry2,
  ipPrefixMask2,
}) {
  return `
configure system security
         cpm-filter
            default-action drop
            ip-filter
                entry ${entry1}
                    description "${descriptionOfEntry1}"
                    match protocol ${protocolType}
                        src-ip ${ipPrefixMask1}
                exit
                entry ${entry2}
                    description "${descriptionOfEntry2}"
                    # To match by IP only, drop the protocol command after "match"
                    match
                        src-ip ${ipPrefixMask2}
                    action permit
                exit
            exit
        exit
exit
`.trim();
}

function buildIsisL2Config({
  areaId,
  interfaceToEdr,
  password,
}) {
  return `
configure router isis
    level-capability level-2
    traffic-engineering
    area-id ${areaId}
    lsp-lifetime 65535
    lsp-refresh-interval 32767 half-lifetime enable
    overload-on-boot timeout 360
    advertise-passive-only
    multi-topology
        ipv6-unicast
    exit
    level 2
        no csnp-authentication
        no psnp-authentication
        wide-metrics-only
    exit
    interface "system"
        level-capability level-2
        passive
        no shutdown
    exit
    interface ${interfaceToEdr}
        level-capability level-2
        hello-authentication-key ${password}
        hello-authentication-type message-digest
        interface-type point-to-point
        bfd-enable ipv4
        level 2
            metric 150000
        exit
        no shutdown
    exit
    no shutdown
    exit all
`.trim();
}

function buildEthCfmServiceConfig({
  domain,
  association,
  adminName,
  remoteMep,
  customer,
  epipe,
  port,
  mep,
  vlan,
  sdp,
  stringName,
  bridgeId,
}) {
  return `
/configure eth-cfm domain ${domain} association ${association} format string name ${stringName} admin-name ${adminName}
/configure eth-cfm domain ${domain} association ${association} bridge-identifier ${bridgeId}
/configure eth-cfm domain ${domain} association ${association} ccm-interval 1
/configure eth-cfm domain ${domain} association ${association} remote-mepid ${remoteMep}
 exit
/configure service epipe ${epipe} customer ${customer} create
/configure service epipe ${epipe} service-mtu 2000
/configure service epipe ${epipe} sap ${port}:${vlan} create
 exit
/configure service epipe ${epipe} sap ${port}:${vlan} eth-cfm mep ${mep} domain ${domain} association ${association} direction up
exit
/configure service epipe ${epipe} sap ${port}:${vlan} eth-cfm mep ${mep} domain ${domain} association ${association} direction up eth-test-enable bit-error-threshold 0
/configure service epipe ${epipe} sap ${port}:${vlan} eth-cfm mep ${mep} domain ${domain} association ${association} direction up eth-test-enable
/configure service epipe ${epipe} sap ${port}:${vlan} eth-cfm mep ${mep} domain ${domain} association ${association}  ccm-enable
exit
/configure service epipe ${epipe} sap ${port}:${vlan} eth-cfm mep ${mep} domain ${domain} association ${association} low-priority-defect noXcon
/configure service epipe ${epipe} sap ${port}:${vlan} eth-cfm mep ${mep} domain ${domain} association ${association} one-way-delay-threshold 0
/configure service epipe ${epipe} sap ${port}:${vlan} eth-cfm mep ${mep} domain ${domain} association ${association} no shutdown
/configure service epipe ${epipe} spoke-sdp ${sdp}:${vlan} vc-type vlan create
/configure service epipe ${epipe} spoke-sdp ${sdp}:${vlan} vc-type vlan endpoint "Insert Z End Device here" create  # for L3 CPEs
/configure service epipe ${epipe} no shutdown
exit
`.trim();
}

function buildVprnDhcpConfig({
  customerId,
  nameForUse,
  serviceName,
  cmlName,
  localAsn,
  rdForCml,
  rtForCml,
  vrfName,
  ipv4Address,
  mask,
  dhcpServerAddress,
  sapPort,
}) {
  return `
configure service
    customer ${customerId} name ${nameForUse} create
            description ${serviceName}
        exit
    vprn 99 name ${cmlName} customer ${customerId} create
        vrf-import BP_EDR-CML_VRF_I
        autonomous-system ${localAsn}
        route-distinguisher ${rdForCml}
        auto-bind-tunnel
            resolution any
           exit
        vrf-target ${rtForCml}
        interface ${vrfName} create
          address ${ipv4Address}/${mask}  #This will be the 1st IP from the /29 assigned
          dhcp server ${dhcpServerAddress}
              lease-populate 5
                gi-address ${ipv4Address} # This will be the 1st IP from the /29 assigned
                no shutdown
            exit
          sap ${sapPort}:99 create
          exit
      exit
      no shutdown
    exit
`.trim();
}

function buildMxPortMirrorConfig({
  mirrorPort,
  mirrorVlan,
  filterPort,
  filterVlan,
}) {
  return `
set interfaces ${mirrorPort} description PortMirror
set interfaces ${mirrorPort} flexible-vlan-tagging
set interfaces ${mirrorPort} encapsulation flexible-ethernet-services
set interfaces ${mirrorPort} unit ${mirrorVlan} encapsulation vlan-bridge
set interfaces ${mirrorPort} unit ${mirrorVlan} vlan-id ${mirrorVlan}

set interfaces ${filterPort} unit ${filterVlan} filter input pm-mpls
set interfaces ${filterPort} unit ${filterVlan} filter output pm-mpls

set forwarding-options port-mirroring input rate 1
set forwarding-options port-mirroring input run-length 1
set forwarding-options port-mirroring input maximum-packet-length 9000

set forwarding-options port-mirroring family any output interface ${mirrorPort}.${mirrorVlan}
set forwarding-options port-mirroring family any output no-filter-check

set firewall family any filter pm-mpls term mirror then count mpls-counter
set firewall family any filter pm-mpls term mirror then port-mirror
set firewall family any filter pm-mpls term mirror then accept

set bridge-domains pmirroring interface ${mirrorPort}.${mirrorVlan}
set bridge-domains pmirroring domain-type bridge
set bridge-domains pmirroring vlan-id ${mirrorVlan}
`.trim();
}

function buildQfxPortMirrorConfig({
  mirrorPort,
  mirrorVlan,
  filterPort,
  filterVlan,
}) {
  return `
set interfaces ${mirrorPort} description Port-mirror
set interfaces ${mirrorPort} unit 0 family ethernet-switching interface-mode trunk
set interfaces ${mirrorPort} unit 0 family ethernet-switching vlan members ${filterVlan}

set forwarding-options analyzer port_mirror output interface ${mirrorPort}.0
set forwarding-options analyzer port_mirror input ingress interface ${filterPort}.0
set forwarding-options analyzer port_mirror input egress interface ${filterPort}.0

set vlans port_mirror vlan-id ${mirrorVlan}
`.trim();
}

function buildAcxPortMirrorConfig({
  mirrorPort,
  mirrorVlan,
  filterPort,
}) {
  return `
set interfaces ${mirrorPort} description Port_Mirror
set interfaces ${mirrorPort} flexible-vlan-tagging
set interfaces ${mirrorPort} encapsulation flexible-ethernet-services
set interfaces ${mirrorPort} unit 0 encapsulation vlan-bridge
set interfaces ${mirrorPort} unit 0 vlan-id ${mirrorVlan}
set vlans ANALYZER_VLAN interface ${mirrorPort}.0

set forwarding-options analyzer portMirror output interface ${mirrorPort}.0
set forwarding-options analyzer portMirror input ingress interface ${filterPort}.0
set forwarding-options analyzer portMirror input egress interface ${filterPort}.0

set vlans ANALYZER_VLAN interface ${filterPort}.0
set vlans ANALYZER_VLAN vlan-id ${mirrorVlan}
`.trim();
}

function buildAdvaEthCfmConfig({
  mdLevel,
  svlan,
  cvlan,
  svlanPcp,
  mepNumber,
  remoteMepNumber,
  mdString,
  manetId,
  manetName,
  cfp,
}) {
  return `
home
configure cfm
 add md ${mdLevel} string ${mdString} ${mdLevel} none
 configure md md-${mdLevel}
  add manet ${manetId} string ${manetName} 1sec ${mepNumber},${remoteMepNumber}
  configure manet manet-${mdLevel}-${manetId}

   #For UNI services.
   add macomp 1 flow-1-1-1-${cfp} ${svlan} none
   #For ENNI services.
   add macomp 1 access-1-1-1-${cfp} ${svlan} none
   #For CBH services.
   add macomp 1 access-1-1-1-${cfp} ${cvlan} none

   add mep ${mepNumber} up access-1-1-1-${cfp} in-service mac-remote-xcon-error enabled ${svlanPcp}
   configure mep mep-${mdLevel}-${manetId}-${mepNumber}
    lm-in-profile enabled
    lm-tx-countallprios enabled
    lm-rx-countallprios enabled
    lm-dualended-countallprios enabled
`.trim();
}

function PortMirror7210Generator({ onClose, onSaveRequest }) {
  const [form, setForm] = useState(PORT_MIRROR_7210_INITIAL_FORM);
  const [generatedConfig, setGeneratedConfig] = useState("");
  const [copyState, setCopyState] = useState("");

  const hasAllRequiredFields = useMemo(
    () => Object.values(form).every((value) => String(value).trim()),
    [form],
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setCopyState("");
  };

  const handleGenerate = () => {
    setGeneratedConfig(buildPortMirror7210Config(form));
    setCopyState("");
  };

  const handleClear = () => {
    setForm(PORT_MIRROR_7210_INITIAL_FORM);
    setGeneratedConfig("");
    setCopyState("");
  };
  const handleLoadSample = () => {
    applySampleForm(setForm, setGeneratedConfig, setCopyState, PORT_MIRROR_7210_SAMPLE_FORM);
  };

  const handleCopy = async () => {
    if (!generatedConfig) return;
    try {
      await navigator.clipboard.writeText(generatedConfig);
      setCopyState("Copied");
    } catch {
      setCopyState("Copy failed");
    }
  };
  const handleDownload = () => {
    downloadConfigAsText("nokia-7210-port-mirror", generatedConfig);
  };

  return (
    <div className="generator-template-workbench">
      <form
        className="config-generator-form"
        onSubmit={createValidatedSubmitHandler(handleGenerate)}
      >
        <label className="editor-field">
          <span>Mirror-dest Port</span>
          <input
            name="portId1"
            value={form.portId1}
            onChange={handleChange}
            placeholder="mirror-dest Port"
            {...getValidationProps("port")}
          />
        </label>

        <label className="editor-field">
          <span>Mirror-src Port</span>
          <input
            name="portId2"
            value={form.portId2}
            onChange={handleChange}
            placeholder="mirror-src Port"
            {...getValidationProps("port")}
          />
        </label>

        <label className="editor-field">
          <span>SID</span>
          <input
            name="sid"
            value={form.sid}
            onChange={handleChange}
            placeholder="SID"
            {...getValidationProps("positiveInteger")}
          />
        </label>

        <div className="config-generator-actions">
          <button type="submit" className="action-button" disabled={!hasAllRequiredFields}>
            Generate configuration
          </button>
          <button type="button" className="action-button action-button-secondary" onClick={handleLoadSample}>
            Load sample
          </button>
          <button type="button" className="action-button action-button-secondary" onClick={handleClear}>
            Clear form
          </button>
        </div>
      </form>

      <div className="config-generator-output-shell">
        <GeneratorOutputHeader
          title="Nokia 7210 Port Mirror"
          generatedConfig={generatedConfig}
          copyState={copyState}
          onCopy={handleCopy}
          onClose={onClose}
          onDownload={handleDownload}
          onSaveToNote={() => onSaveRequest?.({ templateTitle: "Nokia 7210 Port Mirror", generatedConfig })}
        />

        <pre className="config-generator-output">
          {generatedConfig || "Complete the fields and generate the configuration output here."}
        </pre>
      </div>
    </div>
  );
}

function BofGenerator({ onClose, onSaveRequest }) {
  const [form, setForm] = useState(BOF_INITIAL_FORM);
  const [generatedConfig, setGeneratedConfig] = useState("");
  const [copyState, setCopyState] = useState("");

  const hasAllRequiredFields = useMemo(
    () => Object.values(form).every((value) => String(value).trim()),
    [form],
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setCopyState("");
  };

  const handleGenerate = () => {
    setGeneratedConfig(buildBofConfig(form));
    setCopyState("");
  };

  const handleClear = () => {
    setForm(BOF_INITIAL_FORM);
    setGeneratedConfig("");
    setCopyState("");
  };
  const handleLoadSample = () => {
    applySampleForm(setForm, setGeneratedConfig, setCopyState, BOF_SAMPLE_FORM);
  };

  const handleCopy = async () => {
    if (!generatedConfig) return;
    try {
      await navigator.clipboard.writeText(generatedConfig);
      setCopyState("Copied");
    } catch {
      setCopyState("Copy failed");
    }
  };
  const handleDownload = () => {
    downloadConfigAsText("nokia-bof-configuration", generatedConfig);
  };

  return (
    <div className="generator-template-workbench">
      <form
        className="config-generator-form"
        onSubmit={createValidatedSubmitHandler(handleGenerate)}
      >
        <label className="editor-field">
          <span>Image File</span>
          <input
            name="imageFile"
            value={form.imageFile}
            onChange={handleChange}
            placeholder={"cf3:\\images\\TiMOS-SR-20.10.R13"}
          />
        </label>

        <label className="editor-field">
          <span>Config File</span>
          <input
            name="configFile"
            value={form.configFile}
            onChange={handleChange}
            placeholder={"cf3:\\nename.cfg"}
          />
        </label>

        <label className="editor-field">
          <span>DNS Server IP</span>
          <input
            name="dnsServerIp"
            value={form.dnsServerIp}
            onChange={handleChange}
            placeholder="DNSServerIP"
            {...getValidationProps("ipv4")}
          />
        </label>

        <label className="editor-field">
          <span>DNS Name</span>
          <input
            name="dnsName"
            value={form.dnsName}
            onChange={handleChange}
            placeholder="DNSName"
          />
        </label>

        <div className="config-generator-actions">
          <button type="submit" className="action-button" disabled={!hasAllRequiredFields}>
            Generate configuration
          </button>
          <button type="button" className="action-button action-button-secondary" onClick={handleLoadSample}>
            Load sample
          </button>
          <button type="button" className="action-button action-button-secondary" onClick={handleClear}>
            Clear form
          </button>
        </div>
      </form>

      <div className="config-generator-output-shell">
        <GeneratorOutputHeader
          title="BOF Configuration"
          generatedConfig={generatedConfig}
          copyState={copyState}
          onCopy={handleCopy}
          onClose={onClose}
          onDownload={handleDownload}
          onSaveToNote={() => onSaveRequest?.({ templateTitle: "BOF Configuration", generatedConfig })}
        />

        <pre className="config-generator-output">
          {generatedConfig || "Complete the fields and generate the configuration output here."}
        </pre>
      </div>
    </div>
  );
}

function AccessPortGenerator({ onClose, onSaveRequest }) {
  const [form, setForm] = useState(ACCESS_PORT_INITIAL_FORM);
  const [generatedConfig, setGeneratedConfig] = useState("");
  const [copyState, setCopyState] = useState("");

  const hasAllRequiredFields = useMemo(
    () => Object.values(form).every((value) => String(value).trim()),
    [form],
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setCopyState("");
  };

  const handleGenerate = () => {
    setGeneratedConfig(buildAccessPortConfig(form));
    setCopyState("");
  };

  const handleClear = () => {
    setForm(ACCESS_PORT_INITIAL_FORM);
    setGeneratedConfig("");
    setCopyState("");
  };
  const handleLoadSample = () => {
    applySampleForm(setForm, setGeneratedConfig, setCopyState, ACCESS_PORT_SAMPLE_FORM);
  };

  const handleCopy = async () => {
    if (!generatedConfig) return;
    try {
      await navigator.clipboard.writeText(generatedConfig);
      setCopyState("Copied");
    } catch {
      setCopyState("Copy failed");
    }
  };
  const handleDownload = () => {
    downloadConfigAsText("nokia-access-port-configuration", generatedConfig);
  };

  return (
    <div className="generator-template-workbench">
      <form
        className="config-generator-form"
        onSubmit={createValidatedSubmitHandler(handleGenerate)}
      >
        <label className="editor-field">
          <span>CFP</span>
          <input
            name="cfp"
            value={form.cfp}
            onChange={handleChange}
            placeholder="CFP"
            {...getValidationProps("port")}
          />
        </label>

        <label className="editor-field">
          <span>Description</span>
          <input
            name="description"
            value={form.description}
            onChange={handleChange}
            placeholder="Description"
          />
        </label>

        <label className="editor-field">
          <span>MTU</span>
          <input
            name="mtu"
            value={form.mtu}
            onChange={handleChange}
            placeholder="MTU"
            {...getValidationProps("positiveInteger")}
          />
        </label>

        <div className="config-generator-actions">
          <button type="submit" className="action-button" disabled={!hasAllRequiredFields}>
            Generate configuration
          </button>
          <button type="button" className="action-button action-button-secondary" onClick={handleLoadSample}>
            Load sample
          </button>
          <button type="button" className="action-button action-button-secondary" onClick={handleClear}>
            Clear form
          </button>
        </div>
      </form>

      <div className="config-generator-output-shell">
        <GeneratorOutputHeader
          title="Access Port Configuration"
          generatedConfig={generatedConfig}
          copyState={copyState}
          onCopy={handleCopy}
          onClose={onClose}
          onDownload={handleDownload}
          onSaveToNote={() => onSaveRequest?.({ templateTitle: "Access Port Configuration", generatedConfig })}
        />

        <pre className="config-generator-output">
          {generatedConfig || "Complete the fields and generate the configuration output here."}
        </pre>
      </div>
    </div>
  );
}

function ServicePortGenerator({ onClose, onSaveRequest }) {
  const [form, setForm] = useState(SERVICE_PORT_INITIAL_FORM);
  const [generatedConfig, setGeneratedConfig] = useState("");
  const [copyState, setCopyState] = useState("");
  const hasAllRequiredFields = useMemo(() => Object.values(form).every((value) => String(value).trim()), [form]);
  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setCopyState("");
  };
  const handleGenerate = () => {
    setGeneratedConfig(buildServicePortConfig(form));
    setCopyState("");
  };
  const handleClear = () => {
    setForm(SERVICE_PORT_INITIAL_FORM);
    setGeneratedConfig("");
    setCopyState("");
  };
  const handleLoadSample = () => {
    applySampleForm(setForm, setGeneratedConfig, setCopyState, SERVICE_PORT_SAMPLE_FORM);
  };
  const handleCopy = async () => {
    if (!generatedConfig) return;
    try {
      await navigator.clipboard.writeText(generatedConfig);
      setCopyState("Copied");
    } catch {
      setCopyState("Copy failed");
    }
  };
  const handleDownload = () => {
    downloadConfigAsText("nokia-service-port-configuration", generatedConfig);
  };
  return (
    <div className="generator-template-workbench">
      <form className="config-generator-form" onSubmit={createValidatedSubmitHandler(handleGenerate)}>
        <label className="editor-field"><span>CFP</span><input name="cfp" value={form.cfp} onChange={handleChange} placeholder="CFP" {...getValidationProps("port")} /></label>
        <label className="editor-field"><span>Port Description</span><input name="portDescription" value={form.portDescription} onChange={handleChange} placeholder="PortDescription" /></label>
        <div className="config-generator-actions">
          <button type="submit" className="action-button" disabled={!hasAllRequiredFields}>Generate configuration</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleLoadSample}>Load sample</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleClear}>Clear form</button>
        </div>
      </form>
      <div className="config-generator-output-shell">
        <GeneratorOutputHeader title="Service Port Configuration" generatedConfig={generatedConfig} copyState={copyState} onCopy={handleCopy} onClose={onClose} onDownload={handleDownload} onSaveToNote={() => onSaveRequest?.({ templateTitle: "Service Port Configuration", generatedConfig })} />
        <pre className="config-generator-output">{generatedConfig || "Complete the fields and generate the configuration output here."}</pre>
      </div>
    </div>
  );
}

function BgpGenerator({ onClose, onSaveRequest }) {
  const [form, setForm] = useState(BGP_INITIAL_FORM);
  const [generatedConfig, setGeneratedConfig] = useState("");
  const [copyState, setCopyState] = useState("");

  const hasAllRequiredFields = useMemo(
    () => Object.values(form).every((value) => String(value).trim()),
    [form],
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setCopyState("");
  };

  const handleGenerate = () => {
    setGeneratedConfig(buildBgpConfig(form));
    setCopyState("");
  };

  const handleClear = () => {
    setForm(BGP_INITIAL_FORM);
    setGeneratedConfig("");
    setCopyState("");
  };
  const handleLoadSample = () => {
    applySampleForm(setForm, setGeneratedConfig, setCopyState, BGP_SAMPLE_FORM);
  };

  const handleCopy = async () => {
    if (!generatedConfig) return;
    try {
      await navigator.clipboard.writeText(generatedConfig);
      setCopyState("Copied");
    } catch {
      setCopyState("Copy failed");
    }
  };
  const handleDownload = () => {
    downloadConfigAsText("nokia-bgp-configuration", generatedConfig);
  };

  return (
    <div className="generator-template-workbench">
      <form
        className="config-generator-form"
        onSubmit={createValidatedSubmitHandler(handleGenerate)}
      >
        <label className="editor-field">
          <span>Authentication Key</span>
          <input
            name="authenticationKey"
            value={form.authenticationKey}
            onChange={handleChange}
            placeholder="Authentication_Key"
          />
        </label>

        <label className="editor-field">
          <span>ERR1 Loopback</span>
          <input
            name="err1Loopback"
            value={form.err1Loopback}
            onChange={handleChange}
            placeholder="ERR1_Loopback"
            {...getValidationProps("ipv4")}
          />
          <small>Example: `10.10.10.1`</small>
        </label>

        <label className="editor-field">
          <span>ERR1 Name</span>
          <input
            name="err1Name"
            value={form.err1Name}
            onChange={handleChange}
            placeholder="ERR1_Name"
          />
        </label>

        <label className="editor-field">
          <span>System IP</span>
          <input
            name="systemIp"
            value={form.systemIp}
            onChange={handleChange}
            placeholder="System_IP"
            {...getValidationProps("ipv4")}
          />
          <small>Router system address, for example `10.10.10.9`</small>
        </label>

        <label className="editor-field">
          <span>ERR2 Loopback</span>
          <input
            name="err2Loopback"
            value={form.err2Loopback}
            onChange={handleChange}
            placeholder="ERR2_Loopback"
            {...getValidationProps("ipv4")}
          />
          <small>Example: `10.10.10.2`</small>
        </label>

        <label className="editor-field">
          <span>ERR2 Name</span>
          <input
            name="err2Name"
            value={form.err2Name}
            onChange={handleChange}
            placeholder="ERR2_Name"
          />
        </label>

        <label className="editor-field">
          <span>Local Address</span>
          <input
            name="localAddress"
            value={form.localAddress}
            onChange={handleChange}
            placeholder="local_address"
            {...getValidationProps("ipv4")}
          />
          <small>Source IP used toward the neighbor</small>
        </label>

        <div className="config-generator-actions">
          <button type="submit" className="action-button" disabled={!hasAllRequiredFields}>
            Generate configuration
          </button>
          <button type="button" className="action-button action-button-secondary" onClick={handleLoadSample}>
            Load sample
          </button>
          <button type="button" className="action-button action-button-secondary" onClick={handleClear}>
            Clear form
          </button>
        </div>
      </form>

      <div className="config-generator-output-shell">
        <GeneratorOutputHeader
          title="BGP Configuration"
          generatedConfig={generatedConfig}
          copyState={copyState}
          onCopy={handleCopy}
          onClose={onClose}
          onDownload={handleDownload}
          onSaveToNote={() => onSaveRequest?.({ templateTitle: "BGP Configuration", generatedConfig })}
        />

        <pre className="config-generator-output">
          {generatedConfig || "Complete the fields and generate the configuration output here."}
        </pre>
      </div>
    </div>
  );
}

function SnmpGenerator({ onClose, onSaveRequest }) {
  const [form, setForm] = useState(SNMP_INITIAL_FORM);
  const [generatedConfig, setGeneratedConfig] = useState("");
  const [copyState, setCopyState] = useState("");

  const hasAllRequiredFields = useMemo(
    () => Object.values(form).every((value) => String(value).trim()),
    [form],
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setCopyState("");
  };

  const handleGenerate = () => {
    setGeneratedConfig(buildSnmpConfig(form));
    setCopyState("");
  };

  const handleClear = () => {
    setForm(SNMP_INITIAL_FORM);
    setGeneratedConfig("");
    setCopyState("");
  };
  const handleLoadSample = () => {
    applySampleForm(setForm, setGeneratedConfig, setCopyState, SNMP_SAMPLE_FORM);
  };

  const handleCopy = async () => {
    if (!generatedConfig) return;
    try {
      await navigator.clipboard.writeText(generatedConfig);
      setCopyState("Copied");
    } catch {
      setCopyState("Copy failed");
    }
  };
  const handleDownload = () => {
    downloadConfigAsText("nokia-snmp-configuration", generatedConfig);
  };

  return (
    <div className="generator-template-workbench">
      <form
        className="config-generator-form"
        onSubmit={createValidatedSubmitHandler(handleGenerate)}
      >
        <label className="editor-field">
          <span>Read Only Community String</span>
          <input
            name="readOnlyCommunity"
            value={form.readOnlyCommunity}
            onChange={handleChange}
            placeholder="Read Only Community_String"
          />
        </label>

        <label className="editor-field">
          <span>Read Write Community String</span>
          <input
            name="readWriteCommunity"
            value={form.readWriteCommunity}
            onChange={handleChange}
            placeholder="Read Write Only Community_String"
          />
        </label>

        <label className="editor-field">
          <span>Read Write All Community String</span>
          <input
            name="readWriteAllCommunity"
            value={form.readWriteAllCommunity}
            onChange={handleChange}
            placeholder="Read Write All Only CommunityString"
          />
        </label>

        <div className="config-generator-actions">
          <button type="submit" className="action-button" disabled={!hasAllRequiredFields}>
            Generate configuration
          </button>
          <button type="button" className="action-button action-button-secondary" onClick={handleLoadSample}>
            Load sample
          </button>
          <button type="button" className="action-button action-button-secondary" onClick={handleClear}>
            Clear form
          </button>
        </div>
      </form>

      <div className="config-generator-output-shell">
        <GeneratorOutputHeader
          title="SNMP Configuration"
          generatedConfig={generatedConfig}
          copyState={copyState}
          onCopy={handleCopy}
          onClose={onClose}
          onDownload={handleDownload}
          onSaveToNote={() => onSaveRequest?.({ templateTitle: "SNMP Configuration", generatedConfig })}
        />

        <pre className="config-generator-output">
          {generatedConfig || "Complete the fields and generate the configuration output here."}
        </pre>
      </div>
    </div>
  );
}

function SystemGenerator({ onClose, onSaveRequest }) {
  const [form, setForm] = useState(SYSTEM_INITIAL_FORM);
  const [generatedConfig, setGeneratedConfig] = useState("");
  const [copyState, setCopyState] = useState("");

  const hasAllRequiredFields = useMemo(
    () => Object.values(form).every((value) => String(value).trim()),
    [form],
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setCopyState("");
  };

  const handleGenerate = () => {
    setGeneratedConfig(buildSystemConfig(form));
    setCopyState("");
  };

  const handleClear = () => {
    setForm(SYSTEM_INITIAL_FORM);
    setGeneratedConfig("");
    setCopyState("");
  };
  const handleLoadSample = () => {
    applySampleForm(setForm, setGeneratedConfig, setCopyState, SYSTEM_SAMPLE_FORM);
  };

  const handleCopy = async () => {
    if (!generatedConfig) return;
    try {
      await navigator.clipboard.writeText(generatedConfig);
      setCopyState("Copied");
    } catch {
      setCopyState("Copy failed");
    }
  };
  const handleDownload = () => {
    downloadConfigAsText("nokia-system-configuration", generatedConfig);
  };

  return (
    <div className="generator-template-workbench">
      <form
        className="config-generator-form"
        onSubmit={createValidatedSubmitHandler(handleGenerate)}
      >
        <label className="editor-field">
          <span>Router Hostname</span>
          <input
            name="routerHostname"
            value={form.routerHostname}
            onChange={handleChange}
            placeholder="RouterHostname"
          />
        </label>

        <label className="editor-field">
          <span>Charter Contact</span>
          <input
            name="charterContact"
            value={form.charterContact}
            onChange={handleChange}
            placeholder="CharterContact"
          />
        </label>

        <label className="editor-field">
          <span>Router Location</span>
          <input
            name="routerLocation"
            value={form.routerLocation}
            onChange={handleChange}
            placeholder="RouterLocation"
          />
        </label>

        <label className="editor-field">
          <span>CLLI Code Info</span>
          <input
            name="clliCodeInfo"
            value={form.clliCodeInfo}
            onChange={handleChange}
            placeholder="CLLICodeInfo"
          />
        </label>

        <label className="editor-field">
          <span>Coordinates</span>
          <input
            name="coordinates"
            value={form.coordinates}
            onChange={handleChange}
            placeholder="coordinates"
          />
        </label>

        <label className="editor-field">
          <span>Primary NTP Server IP</span>
          <input
            name="primaryNtpServerIp"
            value={form.primaryNtpServerIp}
            onChange={handleChange}
            placeholder="PrimaryNTPServerIP"
            {...getValidationProps("ipv4")}
          />
          <small>Example: `10.1.1.20`</small>
        </label>

        <label className="editor-field">
          <span>Secondary NTP Server IP</span>
          <input
            name="secondaryNtpServerIp"
            value={form.secondaryNtpServerIp}
            onChange={handleChange}
            placeholder="SecondaryNTPServerIP"
            {...getValidationProps("ipv4")}
          />
          <small>Example: `10.1.1.21`</small>
        </label>

        <label className="editor-field">
          <span>Standard Zone Name</span>
          <input
            name="standardZoneName"
            value={form.standardZoneName}
            onChange={handleChange}
            placeholder="StandardZoneName"
          />
        </label>

        <div className="config-generator-actions">
          <button type="submit" className="action-button" disabled={!hasAllRequiredFields}>
            Generate configuration
          </button>
          <button type="button" className="action-button action-button-secondary" onClick={handleLoadSample}>
            Load sample
          </button>
          <button type="button" className="action-button action-button-secondary" onClick={handleClear}>
            Clear form
          </button>
        </div>
      </form>

      <div className="config-generator-output-shell">
        <GeneratorOutputHeader
          title="System Configuration"
          generatedConfig={generatedConfig}
          copyState={copyState}
          onCopy={handleCopy}
          onClose={onClose}
          onDownload={handleDownload}
          onSaveToNote={() => onSaveRequest?.({ templateTitle: "System Configuration", generatedConfig })}
        />

        <pre className="config-generator-output">
          {generatedConfig || "Complete the fields and generate the configuration output here."}
        </pre>
      </div>
    </div>
  );
}

function LoginControlGenerator({ onClose, onSaveRequest }) {
  const [form, setForm] = useState(LOGIN_CONTROL_INITIAL_FORM);
  const [generatedConfig, setGeneratedConfig] = useState("");
  const [copyState, setCopyState] = useState("");
  const hasAllRequiredFields = useMemo(() => Object.values(form).every((value) => String(value).trim()), [form]);
  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setCopyState("");
  };
  const handleGenerate = () => {
    setGeneratedConfig(buildLoginControlConfig(form));
    setCopyState("");
  };
  const handleClear = () => {
    setForm(LOGIN_CONTROL_INITIAL_FORM);
    setGeneratedConfig("");
    setCopyState("");
  };
  const handleLoadSample = () => {
    applySampleForm(setForm, setGeneratedConfig, setCopyState, LOGIN_CONTROL_SAMPLE_FORM);
  };
  const handleCopy = async () => {
    if (!generatedConfig) return;
    try {
      await navigator.clipboard.writeText(generatedConfig);
      setCopyState("Copied");
    } catch {
      setCopyState("Copy failed");
    }
  };
  const handleDownload = () => {
    downloadConfigAsText("nokia-login-control-configuration", generatedConfig);
  };
  return (
    <div className="generator-template-workbench">
      <form className="config-generator-form" onSubmit={createValidatedSubmitHandler(handleGenerate)}>
        <label className="editor-field"><span>Inbound Sessions Allowed</span><input name="inboundSessionsAllowed" value={form.inboundSessionsAllowed} onChange={handleChange} placeholder="NumberofInboundSessionsAllowed" {...getValidationProps("positiveInteger")} /></label>
        <label className="editor-field"><span>Idle Time</span><input name="idleTime" value={form.idleTime} onChange={handleChange} placeholder="IdleTime" {...getValidationProps("positiveInteger")} /></label>
        <label className="editor-field"><span>Pre Login Message</span><input name="preLoginMessage" value={form.preLoginMessage} onChange={handleChange} placeholder="pre_login_message" /></label>
        <label className="editor-field"><span>MOTD</span><input name="motd" value={form.motd} onChange={handleChange} placeholder="motd" /></label>
        <div className="config-generator-actions">
          <button type="submit" className="action-button" disabled={!hasAllRequiredFields}>Generate configuration</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleLoadSample}>Load sample</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleClear}>Clear form</button>
        </div>
      </form>
      <div className="config-generator-output-shell">
        <GeneratorOutputHeader title="Login Control Configuration" generatedConfig={generatedConfig} copyState={copyState} onCopy={handleCopy} onClose={onClose} onDownload={handleDownload} onSaveToNote={() => onSaveRequest?.({ templateTitle: "Login Control Configuration", generatedConfig })} />
        <pre className="config-generator-output">{generatedConfig || "Complete the fields and generate the configuration output here."}</pre>
      </div>
    </div>
  );
}

function TacacsGenerator({ onClose, onSaveRequest }) {
  const [form, setForm] = useState(TACACS_INITIAL_FORM);
  const [generatedConfig, setGeneratedConfig] = useState("");
  const [copyState, setCopyState] = useState("");
  const hasAllRequiredFields = useMemo(() => Object.values(form).every((value) => String(value).trim()), [form]);
  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setCopyState("");
  };
  const handleGenerate = () => {
    setGeneratedConfig(buildTacacsConfig(form));
    setCopyState("");
  };
  const handleClear = () => {
    setForm(TACACS_INITIAL_FORM);
    setGeneratedConfig("");
    setCopyState("");
  };
  const handleLoadSample = () => {
    applySampleForm(setForm, setGeneratedConfig, setCopyState, TACACS_SAMPLE_FORM);
  };
  const handleCopy = async () => {
    if (!generatedConfig) return;
    try {
      await navigator.clipboard.writeText(generatedConfig);
      setCopyState("Copied");
    } catch {
      setCopyState("Copy failed");
    }
  };
  const handleDownload = () => {
    downloadConfigAsText("nokia-tacacs-authentication-configuration", generatedConfig);
  };
  return (
    <div className="generator-template-workbench">
      <form className="config-generator-form" onSubmit={createValidatedSubmitHandler(handleGenerate)}>
        <label className="editor-field"><span>Number of Attempts</span><input name="numberOfAttempts" value={form.numberOfAttempts} onChange={handleChange} placeholder="Number of Attempts" {...getValidationProps("positiveInteger")} /></label>
        <label className="editor-field"><span>Time</span><input name="numberOfTries" value={form.numberOfTries} onChange={handleChange} placeholder="Number of Tries" {...getValidationProps("positiveInteger")} /></label>
        <label className="editor-field"><span>Lockout Duration</span><input name="lockoutDuration" value={form.lockoutDuration} onChange={handleChange} placeholder="Lockout Duration" {...getValidationProps("positiveInteger")} /></label>
        <label className="editor-field"><span>Primary TACACS IP</span><input name="primaryTacacsIp" value={form.primaryTacacsIp} onChange={handleChange} placeholder="Primary TACACS_IP" {...getValidationProps("ipv4")} /><small>Example: `10.20.30.40`</small></label>
        <label className="editor-field"><span>Primary TACACS Password</span><input name="primaryTacacsPassword" value={form.primaryTacacsPassword} onChange={handleChange} placeholder="Primary TACACS Password" /></label>
        <label className="editor-field"><span>Secondary TACACS IP</span><input name="secondaryTacacsIp" value={form.secondaryTacacsIp} onChange={handleChange} placeholder="Secondary TACACS_IP" {...getValidationProps("ipv4")} /><small>Leave a second server IP if you use TACACS redundancy</small></label>
        <label className="editor-field"><span>Secondary TACACS Password</span><input name="secondaryTacacsPassword" value={form.secondaryTacacsPassword} onChange={handleChange} placeholder="Secondary TACACS Password" /></label>
        <div className="config-generator-actions">
          <button type="submit" className="action-button" disabled={!hasAllRequiredFields}>Generate configuration</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleLoadSample}>Load sample</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleClear}>Clear form</button>
        </div>
      </form>
      <div className="config-generator-output-shell">
        <GeneratorOutputHeader title="TACACS and Authentication Configuration" generatedConfig={generatedConfig} copyState={copyState} onCopy={handleCopy} onClose={onClose} onDownload={handleDownload} onSaveToNote={() => onSaveRequest?.({ templateTitle: "TACACS and Authentication Configuration", generatedConfig })} />
        <pre className="config-generator-output">{generatedConfig || "Complete the fields and generate the configuration output here."}</pre>
      </div>
    </div>
  );
}

function CpmFilterGenerator({ onClose, onSaveRequest }) {
  const [form, setForm] = useState(CPM_FILTER_INITIAL_FORM);
  const [generatedConfig, setGeneratedConfig] = useState("");
  const [copyState, setCopyState] = useState("");
  const hasAllRequiredFields = useMemo(() => Object.values(form).every((value) => String(value).trim()), [form]);
  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setCopyState("");
  };
  const handleGenerate = () => {
    setGeneratedConfig(buildCpmFilterConfig(form));
    setCopyState("");
  };
  const handleClear = () => {
    setForm(CPM_FILTER_INITIAL_FORM);
    setGeneratedConfig("");
    setCopyState("");
  };
  const handleLoadSample = () => {
    applySampleForm(setForm, setGeneratedConfig, setCopyState, CPM_FILTER_SAMPLE_FORM);
  };
  const handleCopy = async () => {
    if (!generatedConfig) return;
    try {
      await navigator.clipboard.writeText(generatedConfig);
      setCopyState("Copied");
    } catch {
      setCopyState("Copy failed");
    }
  };
  const handleDownload = () => {
    downloadConfigAsText("nokia-cpm-filter-configuration", generatedConfig);
  };
  return (
    <div className="generator-template-workbench">
      <form className="config-generator-form" onSubmit={createValidatedSubmitHandler(handleGenerate)}>
        <label className="editor-field"><span>Entry 1</span><input name="entry1" value={form.entry1} onChange={handleChange} placeholder="entry1" {...getValidationProps("positiveInteger")} /></label>
        <label className="editor-field"><span>Description Of Entry 1</span><input name="descriptionOfEntry1" value={form.descriptionOfEntry1} onChange={handleChange} placeholder="DescriptionOfEntry1" /></label>
        <label className="editor-field"><span>Protocol Type</span><input name="protocolType" value={form.protocolType} onChange={handleChange} placeholder="ProtocolType" /></label>
        <label className="editor-field"><span>IP Prefix Mask 1</span><input name="ipPrefixMask1" value={form.ipPrefixMask1} onChange={handleChange} placeholder="ip_prefix_mask_1" {...getValidationProps("ipv4OrCidr")} /><small>Example: `10.0.0.0/8` or `192.168.1.1`</small></label>
        <label className="editor-field"><span>Entry 2</span><input name="entry2" value={form.entry2} onChange={handleChange} placeholder="entry2" {...getValidationProps("positiveInteger")} /></label>
        <label className="editor-field"><span>Description Of Entry 2</span><input name="descriptionOfEntry2" value={form.descriptionOfEntry2} onChange={handleChange} placeholder="DescriptionOfEntry2" /></label>
        <label className="editor-field"><span>IP Prefix Mask 2</span><input name="ipPrefixMask2" value={form.ipPrefixMask2} onChange={handleChange} placeholder="ip_prefix_mask_2" {...getValidationProps("ipv4OrCidr")} /><small>Use host or subnet notation depending on the filter entry</small></label>
        <div className="config-generator-actions">
          <button type="submit" className="action-button" disabled={!hasAllRequiredFields}>Generate configuration</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleLoadSample}>Load sample</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleClear}>Clear form</button>
        </div>
      </form>
      <div className="config-generator-output-shell">
        <GeneratorOutputHeader title="CPM-Filter Configuration" generatedConfig={generatedConfig} copyState={copyState} onCopy={handleCopy} onClose={onClose} onDownload={handleDownload} onSaveToNote={() => onSaveRequest?.({ templateTitle: "CPM-Filter Configuration", generatedConfig })} />
        <pre className="config-generator-output">{generatedConfig || "Complete the fields and generate the configuration output here."}</pre>
      </div>
    </div>
  );
}

function IsisL2Generator({ onClose, onSaveRequest }) {
  const [form, setForm] = useState(ISIS_L2_INITIAL_FORM);
  const [generatedConfig, setGeneratedConfig] = useState("");
  const [copyState, setCopyState] = useState("");
  const hasAllRequiredFields = useMemo(() => Object.values(form).every((value) => String(value).trim()), [form]);
  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setCopyState("");
  };
  const handleGenerate = () => {
    setGeneratedConfig(buildIsisL2Config(form));
    setCopyState("");
  };
  const handleClear = () => {
    setForm(ISIS_L2_INITIAL_FORM);
    setGeneratedConfig("");
    setCopyState("");
  };
  const handleLoadSample = () => {
    applySampleForm(setForm, setGeneratedConfig, setCopyState, ISIS_L2_SAMPLE_FORM);
  };
  const handleCopy = async () => {
    if (!generatedConfig) return;
    try {
      await navigator.clipboard.writeText(generatedConfig);
      setCopyState("Copied");
    } catch {
      setCopyState("Copy failed");
    }
  };
  const handleDownload = () => {
    downloadConfigAsText("nokia-isis-l2-configuration", generatedConfig);
  };
  return (
    <div className="generator-template-workbench">
      <form className="config-generator-form" onSubmit={createValidatedSubmitHandler(handleGenerate)}>
        <label className="editor-field"><span>Area ID</span><input name="areaId" value={form.areaId} onChange={handleChange} placeholder="area_id" /></label>
        <label className="editor-field"><span>Interface To EDR</span><input name="interfaceToEdr" value={form.interfaceToEdr} onChange={handleChange} placeholder="INTERFACE_TO_EDR" {...getValidationProps("port")} /><small>Example: `to-ERR-1` or `1/1/c1/1`</small></label>
        <label className="editor-field"><span>Password</span><input name="password" value={form.password} onChange={handleChange} placeholder="PASSWORD" /></label>
        <div className="config-generator-actions">
          <button type="submit" className="action-button" disabled={!hasAllRequiredFields}>Generate configuration</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleLoadSample}>Load sample</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleClear}>Clear form</button>
        </div>
      </form>
      <div className="config-generator-output-shell">
        <GeneratorOutputHeader title="IS-IS L2 Configuration" generatedConfig={generatedConfig} copyState={copyState} onCopy={handleCopy} onClose={onClose} onDownload={handleDownload} onSaveToNote={() => onSaveRequest?.({ templateTitle: "IS-IS L2 Configuration", generatedConfig })} />
        <pre className="config-generator-output">{generatedConfig || "Complete the fields and generate the configuration output here."}</pre>
      </div>
    </div>
  );
}

function EthCfmServiceGenerator({ onClose, onSaveRequest }) {
  const [form, setForm] = useState(ETH_CFM_SERVICE_INITIAL_FORM);
  const [generatedConfig, setGeneratedConfig] = useState("");
  const [copyState, setCopyState] = useState("");
  const hasAllRequiredFields = useMemo(() => Object.values(form).every((value) => String(value).trim()), [form]);
  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setCopyState("");
  };
  const handleGenerate = () => {
    setGeneratedConfig(buildEthCfmServiceConfig(form));
    setCopyState("");
  };
  const handleClear = () => {
    setForm(ETH_CFM_SERVICE_INITIAL_FORM);
    setGeneratedConfig("");
    setCopyState("");
  };
  const handleLoadSample = () => {
    applySampleForm(setForm, setGeneratedConfig, setCopyState, ETH_CFM_SERVICE_SAMPLE_FORM);
  };
  const handleCopy = async () => {
    if (!generatedConfig) return;
    try {
      await navigator.clipboard.writeText(generatedConfig);
      setCopyState("Copied");
    } catch {
      setCopyState("Copy failed");
    }
  };
  const handleDownload = () => {
    downloadConfigAsText("nokia-eth-cfm-service-configuration", generatedConfig);
  };
  return (
    <div className="generator-template-workbench">
      <form className="config-generator-form" onSubmit={createValidatedSubmitHandler(handleGenerate)}>
        <label className="editor-field"><span>Domain</span><input name="domain" value={form.domain} onChange={handleChange} placeholder="Domain" /></label>
        <label className="editor-field"><span>Association</span><input name="association" value={form.association} onChange={handleChange} placeholder="Association" /></label>
        <label className="editor-field"><span>Admin Name</span><input name="adminName" value={form.adminName} onChange={handleChange} placeholder="Admin-name (same-name as association)" /></label>
        <label className="editor-field"><span>Remote MEP</span><input name="remoteMep" value={form.remoteMep} onChange={handleChange} placeholder="Remote-Mep" {...getValidationProps("positiveInteger")} /></label>
        <label className="editor-field"><span>Customer</span><input name="customer" value={form.customer} onChange={handleChange} placeholder="Customer" {...getValidationProps("positiveInteger")} /></label>
        <label className="editor-field"><span>EPIPE</span><input name="epipe" value={form.epipe} onChange={handleChange} placeholder="EPIPE" {...getValidationProps("positiveInteger")} /></label>
        <label className="editor-field"><span>Port</span><input name="port" value={form.port} onChange={handleChange} placeholder="Port" {...getValidationProps("port")} /><small>Example: `1/1/c1/1`</small></label>
        <label className="editor-field"><span>MEP</span><input name="mep" value={form.mep} onChange={handleChange} placeholder="MEP" {...getValidationProps("positiveInteger")} /></label>
        <label className="editor-field"><span>VLAN</span><input name="vlan" value={form.vlan} onChange={handleChange} placeholder="VLAN" {...getValidationProps("vlan")} /><small>Valid range: `1-4094`</small></label>
        <label className="editor-field"><span>SDP</span><input name="sdp" value={form.sdp} onChange={handleChange} placeholder="SDP" {...getValidationProps("positiveInteger")} /><small>Numeric service distribution point ID</small></label>
        <label className="editor-field"><span>String</span><input name="stringName" value={form.stringName} onChange={handleChange} placeholder="String" /></label>
        <label className="editor-field"><span>Bridge ID</span><input name="bridgeId" value={form.bridgeId} onChange={handleChange} placeholder="BridgeId" {...getValidationProps("positiveInteger")} /></label>
        <div className="config-generator-actions">
          <button type="submit" className="action-button" disabled={!hasAllRequiredFields}>Generate configuration</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleLoadSample}>Load sample</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleClear}>Clear form</button>
        </div>
      </form>
      <div className="config-generator-output-shell">
        <GeneratorOutputHeader title="Eth-CFM Service Configuration" generatedConfig={generatedConfig} copyState={copyState} onCopy={handleCopy} onClose={onClose} onDownload={handleDownload} onSaveToNote={() => onSaveRequest?.({ templateTitle: "Eth-CFM Service Configuration", generatedConfig })} />
        <pre className="config-generator-output">{generatedConfig || "Complete the fields and generate the configuration output here."}</pre>
      </div>
    </div>
  );
}

function VprnDhcpGenerator({ onClose, onSaveRequest }) {
  const [form, setForm] = useState(VPRN_DHCP_INITIAL_FORM);
  const [generatedConfig, setGeneratedConfig] = useState("");
  const [copyState, setCopyState] = useState("");
  const hasAllRequiredFields = useMemo(() => Object.values(form).every((value) => String(value).trim()), [form]);
  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setCopyState("");
  };
  const handleGenerate = () => {
    setGeneratedConfig(buildVprnDhcpConfig(form));
    setCopyState("");
  };
  const handleClear = () => {
    setForm(VPRN_DHCP_INITIAL_FORM);
    setGeneratedConfig("");
    setCopyState("");
  };
  const handleLoadSample = () => {
    applySampleForm(setForm, setGeneratedConfig, setCopyState, VPRN_DHCP_SAMPLE_FORM);
  };
  const handleCopy = async () => {
    if (!generatedConfig) return;
    try {
      await navigator.clipboard.writeText(generatedConfig);
      setCopyState("Copied");
    } catch {
      setCopyState("Copy failed");
    }
  };
  const handleDownload = () => {
    downloadConfigAsText("nokia-vprn-dhcp-configuration", generatedConfig);
  };
  return (
    <div className="generator-template-workbench">
      <form className="config-generator-form" onSubmit={createValidatedSubmitHandler(handleGenerate)}>
        <label className="editor-field"><span>CUS ID</span><input name="customerId" value={form.customerId} onChange={handleChange} placeholder="CUS_ID" {...getValidationProps("positiveInteger")} /></label>
        <label className="editor-field"><span>Name For Use</span><input name="nameForUse" value={form.nameForUse} onChange={handleChange} placeholder="name_For_use" /></label>
        <label className="editor-field"><span>Service Name</span><input name="serviceName" value={form.serviceName} onChange={handleChange} placeholder="name_of_the_Service" /></label>
        <label className="editor-field"><span>CML Name</span><input name="cmlName" value={form.cmlName} onChange={handleChange} placeholder="CML_name" /></label>
        <label className="editor-field"><span>Local ASN</span><input name="localAsn" value={form.localAsn} onChange={handleChange} placeholder="Local_ASN" {...getValidationProps("positiveInteger")} /></label>
        <label className="editor-field"><span>RD For CML</span><input name="rdForCml" value={form.rdForCml} onChange={handleChange} placeholder="RD_For_CML" /></label>
        <label className="editor-field"><span>RT For CML</span><input name="rtForCml" value={form.rtForCml} onChange={handleChange} placeholder="RT_For_CML" /></label>
        <label className="editor-field"><span>VRF Name</span><input name="vrfName" value={form.vrfName} onChange={handleChange} placeholder="VRF_name" /></label>
        <label className="editor-field"><span>IPv4 Address</span><input name="ipv4Address" value={form.ipv4Address} onChange={handleChange} placeholder="IPv4_address" {...getValidationProps("ipv4")} /><small>First usable address from the assigned subnet</small></label>
        <label className="editor-field"><span>Mask</span><input name="mask" value={form.mask} onChange={handleChange} placeholder="mask" {...getValidationProps("mask")} /><small>Example: `29` for a `/29`</small></label>
        <label className="editor-field"><span>DHCP Server Address</span><input name="dhcpServerAddress" value={form.dhcpServerAddress} onChange={handleChange} placeholder="dhcp_server_address" {...getValidationProps("ipv4")} /><small>Example: `10.1.1.2`</small></label>
        <label className="editor-field"><span>SAP Port</span><input name="sapPort" value={form.sapPort} onChange={handleChange} placeholder="sap_port" {...getValidationProps("port")} /><small>Example: `1/1/c1/1`</small></label>
        <div className="config-generator-actions">
          <button type="submit" className="action-button" disabled={!hasAllRequiredFields}>Generate configuration</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleLoadSample}>Load sample</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleClear}>Clear form</button>
        </div>
      </form>
      <div className="config-generator-output-shell">
        <GeneratorOutputHeader title="VPRN with DHCP Configuration" generatedConfig={generatedConfig} copyState={copyState} onCopy={handleCopy} onClose={onClose} onDownload={handleDownload} onSaveToNote={() => onSaveRequest?.({ templateTitle: "VPRN with DHCP Configuration", generatedConfig })} />
        <pre className="config-generator-output">{generatedConfig || "Complete the fields and generate the configuration output here."}</pre>
      </div>
    </div>
  );
}

function MxPortMirrorGenerator({ onClose, onSaveRequest }) {
  const [form, setForm] = useState(JUNIPER_PORT_MIRROR_INITIAL_FORM);
  const [generatedConfig, setGeneratedConfig] = useState("");
  const [copyState, setCopyState] = useState("");
  const hasAllRequiredFields = useMemo(() => Object.values(form).every((value) => String(value).trim()), [form]);
  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setCopyState("");
  };
  const handleGenerate = () => {
    setGeneratedConfig(buildMxPortMirrorConfig(form));
    setCopyState("");
  };
  const handleClear = () => {
    setForm(JUNIPER_PORT_MIRROR_INITIAL_FORM);
    setGeneratedConfig("");
    setCopyState("");
  };
  const handleLoadSample = () => {
    applySampleForm(setForm, setGeneratedConfig, setCopyState, JUNIPER_PORT_MIRROR_SAMPLE_FORM);
  };
  const handleCopy = async () => {
    if (!generatedConfig) return;
    try {
      await navigator.clipboard.writeText(generatedConfig);
      setCopyState("Copied");
    } catch {
      setCopyState("Copy failed");
    }
  };
  const handleDownload = () => {
    downloadConfigAsText("juniper-mx-port-mirror", generatedConfig);
  };
  return (
    <div className="generator-template-workbench">
      <form className="config-generator-form" onSubmit={createValidatedSubmitHandler(handleGenerate)}>
        <label className="editor-field"><span>Mirror Port</span><input name="mirrorPort" value={form.mirrorPort} onChange={handleChange} placeholder="Mirror PORT - To STC" {...getValidationProps("port")} /><small>Example: `xe-0/0/49` or `et-0/1/1`</small></label>
        <label className="editor-field"><span>Mirror VLAN</span><input name="mirrorVlan" value={form.mirrorVlan} onChange={handleChange} placeholder="Mirror VLAN" {...getValidationProps("vlan")} /><small>Destination analyzer VLAN ID</small></label>
        <label className="editor-field"><span>Filter Port</span><input name="filterPort" value={form.filterPort} onChange={handleChange} placeholder="Filter Port" {...getValidationProps("port")} /><small>Source interface being mirrored</small></label>
        <label className="editor-field"><span>Filter VLAN</span><input name="filterVlan" value={form.filterVlan} onChange={handleChange} placeholder="Filter VLAN" {...getValidationProps("vlan")} /></label>
        <div className="config-generator-actions">
          <button type="submit" className="action-button" disabled={!hasAllRequiredFields}>Generate configuration</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleLoadSample}>Load sample</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleClear}>Clear form</button>
        </div>
      </form>
      <div className="config-generator-output-shell">
        <GeneratorOutputHeader title="Juniper MX Port Mirror" generatedConfig={generatedConfig} copyState={copyState} onCopy={handleCopy} onClose={onClose} onDownload={handleDownload} onSaveToNote={() => onSaveRequest?.({ templateTitle: "Juniper MX Port Mirror", generatedConfig })} />
        <pre className="config-generator-output">{generatedConfig || "Complete the fields and generate the configuration output here."}</pre>
      </div>
    </div>
  );
}

function QfxPortMirrorGenerator({ onClose, onSaveRequest }) {
  const [form, setForm] = useState(JUNIPER_PORT_MIRROR_INITIAL_FORM);
  const [generatedConfig, setGeneratedConfig] = useState("");
  const [copyState, setCopyState] = useState("");
  const hasAllRequiredFields = useMemo(() => Object.values(form).every((value) => String(value).trim()), [form]);
  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setCopyState("");
  };
  const handleGenerate = () => {
    setGeneratedConfig(buildQfxPortMirrorConfig(form));
    setCopyState("");
  };
  const handleClear = () => {
    setForm(JUNIPER_PORT_MIRROR_INITIAL_FORM);
    setGeneratedConfig("");
    setCopyState("");
  };
  const handleLoadSample = () => {
    applySampleForm(setForm, setGeneratedConfig, setCopyState, JUNIPER_PORT_MIRROR_SAMPLE_FORM);
  };
  const handleCopy = async () => {
    if (!generatedConfig) return;
    try {
      await navigator.clipboard.writeText(generatedConfig);
      setCopyState("Copied");
    } catch {
      setCopyState("Copy failed");
    }
  };
  const handleDownload = () => {
    downloadConfigAsText("juniper-qfx-5100-port-mirror", generatedConfig);
  };
  return (
    <div className="generator-template-workbench">
      <form className="config-generator-form" onSubmit={createValidatedSubmitHandler(handleGenerate)}>
        <label className="editor-field"><span>Mirror Port</span><input name="mirrorPort" value={form.mirrorPort} onChange={handleChange} placeholder="Mirror PORT - To STC" {...getValidationProps("port")} /><small>Example: `xe-0/0/1`</small></label>
        <label className="editor-field"><span>Mirror VLAN</span><input name="mirrorVlan" value={form.mirrorVlan} onChange={handleChange} placeholder="Mirror VLAN" {...getValidationProps("vlan")} /><small>Analyzer or trunk VLAN</small></label>
        <label className="editor-field"><span>Filter Port</span><input name="filterPort" value={form.filterPort} onChange={handleChange} placeholder="Filter Port" {...getValidationProps("port")} /><small>Example: `ge-0/0/9`</small></label>
        <label className="editor-field"><span>Filter VLAN</span><input name="filterVlan" value={form.filterVlan} onChange={handleChange} placeholder="Filter VLAN" {...getValidationProps("vlan")} /></label>
        <div className="config-generator-actions">
          <button type="submit" className="action-button" disabled={!hasAllRequiredFields}>Generate configuration</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleLoadSample}>Load sample</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleClear}>Clear form</button>
        </div>
      </form>
      <div className="config-generator-output-shell">
        <GeneratorOutputHeader title="QFX 5100 Port Mirror" generatedConfig={generatedConfig} copyState={copyState} onCopy={handleCopy} onClose={onClose} onDownload={handleDownload} onSaveToNote={() => onSaveRequest?.({ templateTitle: "QFX 5100 Port Mirror", generatedConfig })} />
        <pre className="config-generator-output">{generatedConfig || "Complete the fields and generate the configuration output here."}</pre>
      </div>
    </div>
  );
}

function AcxPortMirrorGenerator({ onClose, onSaveRequest }) {
  const [form, setForm] = useState(JUNIPER_PORT_MIRROR_INITIAL_FORM);
  const [generatedConfig, setGeneratedConfig] = useState("");
  const [copyState, setCopyState] = useState("");
  const hasAllRequiredFields = useMemo(
    () => [form.mirrorPort, form.mirrorVlan, form.filterPort].every((value) => String(value).trim()),
    [form],
  );
  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setCopyState("");
  };
  const handleGenerate = () => {
    setGeneratedConfig(buildAcxPortMirrorConfig(form));
    setCopyState("");
  };
  const handleClear = () => {
    setForm(JUNIPER_PORT_MIRROR_INITIAL_FORM);
    setGeneratedConfig("");
    setCopyState("");
  };
  const handleLoadSample = () => {
    applySampleForm(setForm, setGeneratedConfig, setCopyState, JUNIPER_PORT_MIRROR_SAMPLE_FORM);
  };
  const handleCopy = async () => {
    if (!generatedConfig) return;
    try {
      await navigator.clipboard.writeText(generatedConfig);
      setCopyState("Copied");
    } catch {
      setCopyState("Copy failed");
    }
  };
  const handleDownload = () => {
    downloadConfigAsText("juniper-acx-port-mirror", generatedConfig);
  };
  return (
    <div className="generator-template-workbench">
      <form className="config-generator-form" onSubmit={createValidatedSubmitHandler(handleGenerate)}>
        <label className="editor-field"><span>Mirror Port</span><input name="mirrorPort" value={form.mirrorPort} onChange={handleChange} placeholder="Mirror PORT - To STC" {...getValidationProps("port")} /><small>Example: `et-0/1/1`</small></label>
        <label className="editor-field"><span>Mirror VLAN</span><input name="mirrorVlan" value={form.mirrorVlan} onChange={handleChange} placeholder="Mirror VLAN" {...getValidationProps("vlan")} /><small>Used for `ANALYZER_VLAN`</small></label>
        <label className="editor-field"><span>Filter Port</span><input name="filterPort" value={form.filterPort} onChange={handleChange} placeholder="Filter Port" {...getValidationProps("port")} /><small>Example: `ae42`</small></label>
        <label className="editor-field"><span>Filter VLAN</span><input name="filterVlan" value={form.filterVlan} onChange={handleChange} placeholder="Filter VLAN | may not use" {...getValidationProps("vlan", { required: false })} /></label>
        <div className="config-generator-actions">
          <button type="submit" className="action-button" disabled={!hasAllRequiredFields}>Generate configuration</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleLoadSample}>Load sample</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleClear}>Clear form</button>
        </div>
      </form>
      <div className="config-generator-output-shell">
        <GeneratorOutputHeader title="Juniper ACX Port Mirror" generatedConfig={generatedConfig} copyState={copyState} onCopy={handleCopy} onClose={onClose} onDownload={handleDownload} onSaveToNote={() => onSaveRequest?.({ templateTitle: "Juniper ACX Port Mirror", generatedConfig })} />
        <pre className="config-generator-output">{generatedConfig || "Complete the fields and generate the configuration output here."}</pre>
      </div>
    </div>
  );
}

function AdvaEthCfmGenerator({ onClose, onSaveRequest }) {
  const [form, setForm] = useState(ADVA_ETH_CFM_INITIAL_FORM);
  const [generatedConfig, setGeneratedConfig] = useState("");
  const [copyState, setCopyState] = useState("");
  const hasAllRequiredFields = useMemo(() => Object.values(form).every((value) => String(value).trim()), [form]);
  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setCopyState("");
  };
  const handleGenerate = () => {
    setGeneratedConfig(buildAdvaEthCfmConfig(form));
    setCopyState("");
  };
  const handleClear = () => {
    setForm(ADVA_ETH_CFM_INITIAL_FORM);
    setGeneratedConfig("");
    setCopyState("");
  };
  const handleLoadSample = () => {
    applySampleForm(setForm, setGeneratedConfig, setCopyState, ADVA_ETH_CFM_SAMPLE_FORM);
  };
  const handleCopy = async () => {
    if (!generatedConfig) return;
    try {
      await navigator.clipboard.writeText(generatedConfig);
      setCopyState("Copied");
    } catch {
      setCopyState("Copy failed");
    }
  };
  const handleDownload = () => {
    downloadConfigAsText("adva-ge114pro-eth-cfm", generatedConfig);
  };
  return (
    <div className="generator-template-workbench">
      <form className="config-generator-form" onSubmit={createValidatedSubmitHandler(handleGenerate)}>
        <label className="editor-field"><span>MD Level</span><input name="mdLevel" value={form.mdLevel} onChange={handleChange} placeholder="MD level - use 4 for CPEs" {...getValidationProps("positiveInteger")} /></label>
        <label className="editor-field"><span>SVLAN</span><input name="svlan" value={form.svlan} onChange={handleChange} placeholder="SVLAN" {...getValidationProps("vlan")} /><small>Service VLAN ID</small></label>
        <label className="editor-field"><span>CVLAN</span><input name="cvlan" value={form.cvlan} onChange={handleChange} placeholder="CVLAN" {...getValidationProps("vlan")} /><small>Customer VLAN ID when used</small></label>
        <label className="editor-field"><span>SVLAN PCP</span><input name="svlanPcp" value={form.svlanPcp} onChange={handleChange} placeholder="svlanpcp" {...getValidationProps("positiveInteger")} /></label>
        <label className="editor-field"><span>MEP Number</span><input name="mepNumber" value={form.mepNumber} onChange={handleChange} placeholder="MEP Number" {...getValidationProps("positiveInteger")} /><small>Local maintenance end point ID</small></label>
        <label className="editor-field"><span>Remote MEP Number</span><input name="remoteMepNumber" value={form.remoteMepNumber} onChange={handleChange} placeholder="Remote MEP number" {...getValidationProps("positiveInteger")} /><small>Peer MEP to monitor</small></label>
        <label className="editor-field"><span>MD String</span><input name="mdString" value={form.mdString} onChange={handleChange} placeholder="String" /></label>
        <label className="editor-field"><span>Manet ID</span><input name="manetId" value={form.manetId} onChange={handleChange} placeholder="Manet Id" {...getValidationProps("positiveInteger")} /></label>
        <label className="editor-field"><span>MA Name</span><input name="manetName" value={form.manetName} onChange={handleChange} placeholder="MA Name" /></label>
        <label className="editor-field"><span>CFP</span><input name="cfp" value={form.cfp} onChange={handleChange} placeholder="CFP # example 3" {...getValidationProps("positiveInteger")} /></label>
        <label className="editor-field"><span>Flow Number</span><input name="flowNumber" value={form.flowNumber} onChange={handleChange} placeholder="Flow Number" {...getValidationProps("positiveInteger")} /></label>
        <div className="config-generator-actions">
          <button type="submit" className="action-button" disabled={!hasAllRequiredFields}>Generate configuration</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleLoadSample}>Load sample</button>
          <button type="button" className="action-button action-button-secondary" onClick={handleClear}>Clear form</button>
        </div>
      </form>
      <div className="config-generator-output-shell">
        <GeneratorOutputHeader title="Adva GE114Pro Eth-CFM" generatedConfig={generatedConfig} copyState={copyState} onCopy={handleCopy} onClose={onClose} onDownload={handleDownload} onSaveToNote={() => onSaveRequest?.({ templateTitle: "Adva GE114Pro Eth-CFM", generatedConfig })} />
        <pre className="config-generator-output">{generatedConfig || "Complete the fields and generate the configuration output here."}</pre>
      </div>
    </div>
  );
}

export default function ConfigGeneratorView() {
  const [customTemplates, setCustomTemplates] = useState([]);
  const [customTemplateGroups, setCustomTemplateGroups] = useState([]);
  const [hiddenBuiltInTemplates, setHiddenBuiltInTemplates] = useState(() => readHiddenBuiltInTemplates());
  const [templateOrderPrefs, setTemplateOrderPrefs] = useState(() => readTemplateOrderPrefs());
  const [customTemplateLibraryReady, setCustomTemplateLibraryReady] = useState(false);
  const [showCreateTemplateDialog, setShowCreateTemplateDialog] = useState(false);
  const [showCreateTemplateGroupDialog, setShowCreateTemplateGroupDialog] = useState(false);
  const [customTemplateDialogMode, setCustomTemplateDialogMode] = useState("create");
  const [customTemplateError, setCustomTemplateError] = useState("");
  const [customTemplateGroupError, setCustomTemplateGroupError] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [customTemplateForm, setCustomTemplateForm] = useState({
    originalName: "",
    name: "",
    vendorLabel: "Custom",
    groupLabel: "Templates",
    subGroupLabel: "",
    detail: "",
    fieldSchema: "",
    outputTemplate: "",
  });
  const [customTemplateGroupForm, setCustomTemplateGroupForm] = useState({
    vendorLabel: "Custom",
    label: "",
  });
  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [templateQuery, setTemplateQuery] = useState("");
  const [draggedTemplateKey, setDraggedTemplateKey] = useState("");
  const [templateDropTargetKey, setTemplateDropTargetKey] = useState("");
  const [saveDraft, setSaveDraft] = useState(null);
  const [saveTargets, setSaveTargets] = useState([]);
  const [saveTargetsLoading, setSaveTargetsLoading] = useState(false);
  const [saveTargetEntries, setSaveTargetEntries] = useState([]);
  const [saveTargetEntriesLoading, setSaveTargetEntriesLoading] = useState(false);
  const [saveMode, setSaveMode] = useState("existing");
  const [saveTitleQuery, setSaveTitleQuery] = useState("");
  const [saveForm, setSaveForm] = useState({
    groupId: "",
    groupTitle: "",
    entryTitle: "",
    folderPath: "",
    tags: "",
    existingAction: "new-entry",
    entryId: "",
  });
  const [saveErrorMessage, setSaveErrorMessage] = useState("");
  const [saveSuccessMessage, setSaveSuccessMessage] = useState("");
  const [savePending, setSavePending] = useState(false);
  const [currentUserLabel, setCurrentUserLabel] = useState("");
  const generatorCatalog = useMemo(
    () => buildCatalogWithCustomTemplates(
      buildCatalogWithCustomTemplates(GENERATOR_CATALOG, recoveredTemplates, []),
      customTemplates, customTemplateGroups, hiddenBuiltInTemplates,
    ),
    [customTemplateGroups, customTemplates, hiddenBuiltInTemplates],
  );

  const selectedVendor = useMemo(
    () => generatorCatalog.find((vendor) => vendor.id === selectedVendorId) || null,
    [generatorCatalog, selectedVendorId],
  );
  const selectedVendorTemplateGroups = useMemo(
    () => applyTemplateOrder(getVendorTemplateGroups(selectedVendor), selectedVendorId, templateOrderPrefs),
    [selectedVendor, selectedVendorId, templateOrderPrefs],
  );
  const normalizedTemplateQuery = templateQuery.trim().toLowerCase();
  const filteredVendorTemplateGroups = useMemo(() => {
    if (!normalizedTemplateQuery) return selectedVendorTemplateGroups;
    return selectedVendorTemplateGroups
      .map((group) => ({
        ...group,
        templates: group.templates.filter((template) =>
          template.toLowerCase().includes(normalizedTemplateQuery),
        ),
      }))
      .filter((group) => group.templates.length > 0);
  }, [normalizedTemplateQuery, selectedVendorTemplateGroups]);
  const selectedRecoveredTemplate = recoveredTemplates.find((template) =>
    template.vendorId === selectedVendorId && template.name === selectedTemplate);
  const isPortMirror7210 = selectedVendorId === "nokia" && selectedTemplate === "Port Mirror 7210";
  const isBofConfiguration = selectedVendorId === "nokia" && selectedTemplate === "BOF configuration";
  const isAccessPortConfiguration = selectedVendorId === "nokia" && selectedTemplate === "Access Port configuration";
  const isServicePortConfiguration = selectedVendorId === "nokia" && selectedTemplate === "Service Port Configuration";
  const isBgpConfiguration = selectedVendorId === "nokia" && selectedTemplate === "BGP Configuration";
  const isSnmpConfiguration = selectedVendorId === "nokia" && selectedTemplate === "SNMP Configuration";
  const isSystemConfiguration = selectedVendorId === "nokia" && selectedTemplate === "System Configuration";
  const isLoginControlConfiguration = selectedVendorId === "nokia" && selectedTemplate === "Login Control Configuration";
  const isTacacsAuthenticationConfiguration = selectedVendorId === "nokia" && selectedTemplate === "TACACS and Authentication Configuration";
  const isCpmFilterConfiguration = selectedVendorId === "nokia" && selectedTemplate === "CPM-Filter Configuration";
  const isIsisL2Configuration = selectedVendorId === "nokia" && selectedTemplate === "IS-IS L2 Configuration";
  const isEthCfmServiceConfiguration = selectedVendorId === "nokia" && selectedTemplate === "Eth-CFM Service Configuration";
  const isVprnWithDhcpConfiguration = selectedVendorId === "nokia" && selectedTemplate === "VPRN with DHCP Configuration";
  const isMxPortMirror = selectedVendorId === "juniper" && selectedTemplate === "MX-Port Mirror";
  const isQfxPortMirror = selectedVendorId === "juniper" && selectedTemplate === "QFX 5100 Port Mirror";
  const isJuniperAcxPortMirror = selectedVendorId === "juniper" && selectedTemplate === "Juniper ACX Port Mirror Configuration";
  const isAdvaEthCfm = selectedVendorId === "adva-nid" && selectedTemplate === "AdvaGE114Pro-Eth-CFM";
  const selectedCustomTemplate = useMemo(
    () => customTemplates.find((template) => (
      (template.vendorId || createVendorIdFromLabel(template.vendorLabel)) === selectedVendorId
      && template.name === selectedTemplate
    )) || null,
    [customTemplates, selectedTemplate, selectedVendorId],
  );
  const saveFolderOptions = useMemo(
    () => collectFolderPaths(saveTargetEntries),
    [saveTargetEntries],
  );
  const filteredSaveTargets = useMemo(() => {
    const normalizedQuery = saveTitleQuery.trim().toLowerCase();
    if (!normalizedQuery) return saveTargets;
    return saveTargets.filter((title) => String(title?.title || "").toLowerCase().includes(normalizedQuery));
  }, [saveTargets, saveTitleQuery]);
  const selectedSaveTarget = useMemo(
    () => saveTargets.find((title) => title.id === saveForm.groupId) || null,
    [saveTargets, saveForm.groupId],
  );
  const selectedAppendEntry = useMemo(
    () => saveTargetEntries.find((entry) => entry.id === saveForm.entryId) || null,
    [saveTargetEntries, saveForm.entryId],
  );
  const selectedVendorGroupLabels = useMemo(
    () => getVendorTemplateGroups(selectedVendor).map((group) => group.label),
    [selectedVendor],
  );
  const selectedVendorCustomGroupLabels = useMemo(
    () => new Set(
      customTemplateGroups
        .filter((group) => (group.vendorId || createVendorIdFromLabel(group.vendorLabel)) === selectedVendorId)
        .map((group) => String(group.label || "").trim())
        .filter(Boolean),
    ),
    [customTemplateGroups, selectedVendorId],
  );

  useEffect(() => {
    let ignore = false;
    const loadCurrentUser = async () => {
      try {
        const res = await api.get("/api/auth/me");
        if (ignore) return;
        const user = res.data?.user || null;
        setCurrentUserLabel(user?.name || user?.email || "");
      } catch {
        if (!ignore) setCurrentUserLabel("");
      }
    };
    loadCurrentUser();
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    let ignore = false;

    const syncCustomTemplateLibrary = async () => {
      const localTemplates = readCustomTemplates();
      const localGroups = readCustomTemplateGroups();

      try {
        const res = await api.get("/api/generator/templates");
        if (ignore) return;

        let remoteTemplates = Array.isArray(res.data?.templates) ? res.data.templates : [];
        let remoteGroups = Array.isArray(res.data?.groups) ? res.data.groups : [];

        const serverWasEmpty = !remoteTemplates.length && !remoteGroups.length;
        const missing = getMissingBrowserTemplates(localTemplates, localGroups, remoteTemplates, remoteGroups);
        if (missing.templates.length || missing.groups.length) {
          const migrated = await api.post("/api/generator/templates/migrate", {
            templates: missing.templates,
            groups: missing.groups,
          });
          if (ignore) return;
          remoteTemplates = Array.isArray(migrated.data?.templates) ? migrated.data.templates : [];
          remoteGroups = Array.isArray(migrated.data?.groups) ? migrated.data.groups : [];
          if (serverWasEmpty) clearCustomTemplateStorage();
        }

        setCustomTemplates(remoteTemplates);
        setCustomTemplateGroups(remoteGroups);
      } catch {
        if (ignore) return;
        setCustomTemplates(localTemplates);
        setCustomTemplateGroups(localGroups);
      } finally {
        if (!ignore) setCustomTemplateLibraryReady(true);
      }
    };

    syncCustomTemplateLibrary();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    const handleKeydown = (event) => {
      const shortcutPressed = event.metaKey || event.ctrlKey;
      const modalOpen = Boolean(saveDraft);

      if (shortcutPressed && !event.shiftKey && event.key.toLowerCase() === "s") {
        event.preventDefault();
        if (modalOpen) {
          clickElement('.generator-save-dialog button[data-shortcut="save-note"]');
          return;
        }
        clickElement('.config-generator-output-controls button[data-shortcut="save-output"]');
        return;
      }

      if (shortcutPressed && event.shiftKey && event.key.toLowerCase() === "c") {
        event.preventDefault();
        const clicked = clickElement('.config-generator-output-controls button[data-shortcut="copy-close"]');
        if (clicked) {
          window.setTimeout(() => {
            setSelectedTemplate("");
          }, 0);
        }
        return;
      }

      if (event.key === "Enter" && !shortcutPressed && !event.shiftKey && !event.altKey) {
        if (modalOpen || !selectedTemplate || !isEditableShortcutTarget(event.target)) return;
        if (String(event.target.tagName || "").toLowerCase() === "textarea") return;
        event.preventDefault();
        clickElement(".generator-template-detail .config-generator-form button[type=\"submit\"]");
      }
    };

    window.addEventListener("keydown", handleKeydown);
    return () => {
      window.removeEventListener("keydown", handleKeydown);
    };
  }, [saveDraft, selectedTemplate]);

  useEffect(() => {
    if (!saveDraft) return undefined;
    let ignore = false;
    const loadTargets = async () => {
      setSaveTargetsLoading(true);
      try {
        const res = await api.get("/api/notes?limit=100&offset=0");
        if (!ignore) {
          setSaveTargets(res.data.items || []);
          setSaveErrorMessage("");
        }
      } catch {
        if (!ignore) {
          setSaveTargets([]);
          setSaveErrorMessage("Could not load existing titles.");
        }
      } finally {
        if (!ignore) setSaveTargetsLoading(false);
      }
    };
    loadTargets();
    return () => {
      ignore = true;
    };
  }, [saveDraft]);

  useEffect(() => {
    if (!saveDraft || saveMode !== "existing" || !saveForm.groupId) {
      setSaveTargetEntries([]);
      setSaveTargetEntriesLoading(false);
      return undefined;
    }
    let ignore = false;
    const loadTargetEntries = async () => {
      setSaveTargetEntriesLoading(true);
      try {
        const res = await api.get(`/api/notes/${saveForm.groupId}`);
        if (!ignore) {
          setSaveTargetEntries(res.data?.entries || []);
        }
      } catch {
        if (!ignore) {
          setSaveTargetEntries([]);
          setSaveErrorMessage("Could not load notes for that title.");
        }
      } finally {
        if (!ignore) setSaveTargetEntriesLoading(false);
      }
    };
    loadTargetEntries();
    return () => {
      ignore = true;
    };
  }, [saveDraft, saveMode, saveForm.groupId]);

  useEffect(() => {
    if (!saveDraft) return;
    if (!saveTargetsLoading && saveForm.groupId && !saveTargets.some((title) => title.id === saveForm.groupId)) {
      setSaveForm((current) => ({ ...current, groupId: "", entryId: "" }));
    }
  }, [saveDraft, saveTargets, saveTargetsLoading, saveForm.groupId]);

  useEffect(() => {
    if (saveForm.existingAction === "append-entry" && saveForm.entryId && !saveTargetEntries.some((entry) => entry.id === saveForm.entryId)) {
      setSaveForm((current) => ({ ...current, entryId: "" }));
    }
  }, [saveForm.entryId, saveForm.existingAction, saveTargetEntries]);

  useEffect(() => {
    if (!saveDraft) return;
    if (saveMode === "new" && saveForm.folderPath) {
      setSaveForm((current) => ({ ...current, folderPath: "" }));
      return;
    }
    if (
      saveMode === "existing"
      && saveForm.existingAction !== "append-entry"
      && saveForm.folderPath
      && saveForm.groupId
      && !saveFolderOptions.includes(saveForm.folderPath)
    ) {
      setSaveForm((current) => ({ ...current, folderPath: "" }));
    }
  }, [saveDraft, saveForm.folderPath, saveForm.existingAction, saveForm.groupId, saveMode, saveFolderOptions]);

  const openSaveDialog = ({ templateTitle, generatedConfig, vendorLabel: explicitVendorLabel }) => {
    const vendorLabel = explicitVendorLabel || selectedVendor?.label || "Generator";
    const vendorTag = vendorLabel.toLowerCase().replace(/\s+/g, "-");
    const templateKey = buildGeneratorTemplateKey(vendorLabel, templateTitle);
    const rememberedPrefs = readGeneratorSavePrefs() || { global: {}, templates: {} };
    const rememberedTemplate = rememberedPrefs.templates?.[templateKey] || {};
    const rememberedGlobal = rememberedPrefs.global || {};
    const remembered = { ...rememberedGlobal, ...rememberedTemplate };
    const defaultSaveMode = remembered.groupId ? "existing" : remembered.saveMode === "new" ? "new" : "existing";
    setSaveTargetsLoading(true);
    setSaveDraft({ templateTitle, generatedConfig, vendorLabel });
    setSaveMode(defaultSaveMode);
    setSaveTitleQuery("");
    setSaveTargetEntries([]);
    setSaveForm({
      groupId: remembered.groupId || "",
      groupTitle: remembered.groupTitle || `${vendorLabel} Configurations`,
      entryTitle: remembered.entryTitle || `${templateTitle} output`,
      folderPath: remembered.folderPath || "",
      tags: remembered.tags || `generator, ${vendorTag}`,
      existingAction: remembered.existingAction || "new-entry",
      entryId: remembered.entryId || "",
    });
    setSaveErrorMessage("");
    setSaveSuccessMessage("");
  };

  const openCreateTemplateDialog = () => {
    setCustomTemplateDialogMode("create");
    setCustomTemplateForm({
      originalName: "",
      name: "",
      vendorLabel: selectedVendor?.label || "Custom",
      groupLabel: "Templates",
      subGroupLabel: "",
      detail: "",
      fieldSchema: "",
      outputTemplate: "",
    });
    setCustomTemplateError("");
    setShowCreateTemplateDialog(true);
  };

  const openCreateTemplateGroupDialog = () => {
    setCustomTemplateGroupForm({
      vendorLabel: selectedVendor?.label || "Custom",
      label: "",
    });
    setCustomTemplateGroupError("");
    setShowCreateTemplateGroupDialog(true);
  };

  const openEditTemplateDialog = (template) => {
    if (!template) return;
    setCustomTemplateDialogMode("edit");
    const { groupLabel, subGroupLabel } = splitGroupLabelPath(template.groupLabel || "Templates");
    setCustomTemplateForm({
      originalName: template.name || "",
      name: template.name || "",
      vendorLabel: template.vendorLabel || selectedVendor?.label || "Custom",
      groupLabel,
      subGroupLabel,
      detail: template.detail || "",
      fieldSchema: serializeCustomTemplateFields(template.fields),
      outputTemplate: template.outputTemplate || "",
    });
    setCustomTemplateError("");
    setShowCreateTemplateDialog(true);
  };

  const openEditBuiltInTemplateDialog = (vendorId, vendorLabel, groupLabel, templateName) => {
    const recovered = recoveredTemplates.find((template) => template.vendorId === vendorId && template.name === templateName);
    setCustomTemplateDialogMode("edit-built-in");
    const { groupLabel: topLevelGroupLabel, subGroupLabel } = splitGroupLabelPath(groupLabel || "Templates");
    setCustomTemplateForm({
      originalName: templateName,
      name: templateName,
      vendorLabel,
      groupLabel: topLevelGroupLabel,
      subGroupLabel,
      detail: "",
      fieldSchema: "",
      outputTemplate: "",
      ...(recovered ? {
        detail: recovered.detail,
        fieldSchema: serializeCustomTemplateFields(recovered.fields),
        outputTemplate: recovered.outputTemplate,
      } : {}),
      builtInTemplateKey: buildBuiltInTemplateKey(vendorId, templateName),
    });
    setCustomTemplateError("");
    setShowCreateTemplateDialog(true);
  };

  const closeCreateTemplateDialog = () => {
    setShowCreateTemplateDialog(false);
    setCustomTemplateError("");
  };

  const closeCreateTemplateGroupDialog = () => {
    setShowCreateTemplateGroupDialog(false);
    setCustomTemplateGroupError("");
  };

  const handleCreateTemplate = async () => {
    const name = customTemplateForm.name.trim();
    const vendorLabel = customTemplateForm.vendorLabel.trim() || "Custom";
    const groupLabel = buildGroupLabelPath(customTemplateForm.groupLabel, customTemplateForm.subGroupLabel);
    const detail = customTemplateForm.detail.trim();
    const outputTemplate = normalizeCustomTemplateOutput(customTemplateForm.outputTemplate.trim());

    if (!name) {
      setCustomTemplateError("Template name is required.");
      return;
    }
    if (!outputTemplate) {
      setCustomTemplateError("Output template is required.");
      return;
    }

    try {
      const fields = parseCustomTemplateFields(customTemplateForm.fieldSchema);
      const vendorId = createVendorIdFromLabel(vendorLabel);
      const existingGroupLabels = new Set(getVendorTemplateGroups(generatorCatalog.find((vendor) => vendor.id === vendorId) || null).map((group) => group.label));
      let nextGroups = customTemplateGroups;

      if (!existingGroupLabels.has(groupLabel)) {
        const groupRes = await api.post("/api/generator/template-groups", {
          vendorId,
          vendorLabel,
          label: groupLabel,
        });
        const savedGroup = groupRes.data?.group;
        if (savedGroup) {
          nextGroups = customTemplateGroups.some((group) => (
            (group.vendorId || createVendorIdFromLabel(group.vendorLabel)) === vendorId
            && String(group.label || "").trim() === groupLabel
          ))
            ? customTemplateGroups
            : [...customTemplateGroups, savedGroup];
          setCustomTemplateGroups(nextGroups);
        }
      }

      const res = await api.post("/api/generator/templates", {
        vendorId,
        vendorLabel,
        name,
        previousName: customTemplateDialogMode === "edit" ? customTemplateForm.originalName || name : "",
        groupLabel,
        detail,
        fields,
        outputTemplate,
      });
      const savedTemplate = res.data?.template;
      if (!savedTemplate) {
        throw new Error(`Could not ${customTemplateDialogMode === "edit" ? "update" : "create"} template.`);
      }

      const nextTemplates = customTemplates.filter((template) => !(
        getCustomTemplateVendorId(template) === vendorId
        && template.name === (customTemplateDialogMode === "edit" ? customTemplateForm.originalName || name : name)
      ));
      nextTemplates.push(savedTemplate);

      setCustomTemplates(nextTemplates);
      if (customTemplateDialogMode === "edit-built-in" && customTemplateForm.name.trim() !== customTemplateForm.originalName) {
        const nextHiddenTemplates = [...new Set([...hiddenBuiltInTemplates, customTemplateForm.builtInTemplateKey])];
        setHiddenBuiltInTemplates(nextHiddenTemplates);
        writeHiddenBuiltInTemplates(nextHiddenTemplates);
      }
      setSelectedVendorId(vendorId);
      setSelectedTemplate(name);
      setTemplateQuery("");
      setCustomTemplateError("");
      setShowCreateTemplateDialog(false);
    } catch (error) {
      setCustomTemplateError(error.response?.data?.error || error.message || `Could not ${customTemplateDialogMode === "edit" ? "update" : "create"} template.`);
    }
  };

  const handleCreateTemplateGroup = async () => {
    const vendorLabel = customTemplateGroupForm.vendorLabel.trim() || "Custom";
    const vendorId = createVendorIdFromLabel(vendorLabel);
    const label = customTemplateGroupForm.label.trim();
    if (!label) {
      setCustomTemplateGroupError("Section label is required.");
      return;
    }

    const existsInCatalog = generatorCatalog.some((vendor) => (
      vendor.id === vendorId && getVendorTemplateGroups(vendor).some((group) => group.label === label)
    ));
    if (existsInCatalog) {
      setCustomTemplateGroupError("That section already exists for this vendor.");
      return;
    }

    try {
      const res = await api.post("/api/generator/template-groups", {
        vendorId,
        vendorLabel,
        label,
      });
      const savedGroup = res.data?.group;
      if (!savedGroup) {
        throw new Error("Could not create section.");
      }

      setCustomTemplateGroups((current) => [...current, savedGroup]);
      setSelectedVendorId(vendorId);
      setCustomTemplateGroupError("");
      setShowCreateTemplateGroupDialog(false);
    } catch (error) {
      setCustomTemplateGroupError(error.response?.data?.error || error.message || "Could not create section.");
    }
  };

  const handleDeleteCustomTemplateByName = async (vendorId, templateName) => {
    try {
      await api.delete(`/api/generator/templates/${encodeURIComponent(vendorId)}/${encodeURIComponent(templateName)}`);
      const nextTemplates = customTemplates.filter((template) => !(
        (template.vendorId || createVendorIdFromLabel(template.vendorLabel)) === vendorId
        && template.name === templateName
      ));

      setCustomTemplates(nextTemplates);
      if (selectedVendorId === vendorId && selectedTemplate === templateName) {
        setSelectedTemplate("");
      }
      setTemplateQuery("");
    } catch (error) {
      setCustomTemplateError(error.response?.data?.error || error.message || "Could not delete template.");
    }
  };

  const handleDeleteCustomTemplate = () => {
    if (!selectedCustomTemplate) return;
    requestTemplateDelete(
      "custom-template",
      selectedCustomTemplate.vendorId || createVendorIdFromLabel(selectedCustomTemplate.vendorLabel),
      selectedCustomTemplate.name,
    );
  };

  const handleDeleteBuiltInTemplate = (vendorId, templateName) => {
    const templateKey = buildBuiltInTemplateKey(vendorId, templateName);
    const nextHiddenTemplates = [...new Set([...hiddenBuiltInTemplates, templateKey])];
    setHiddenBuiltInTemplates(nextHiddenTemplates);
    writeHiddenBuiltInTemplates(nextHiddenTemplates);
    if (selectedVendorId === vendorId && selectedTemplate === templateName) {
      setSelectedTemplate("");
    }
    setTemplateQuery("");
  };

  const requestTemplateDelete = (type, vendorId, templateName) => {
    setPendingDelete({ type, vendorId, templateName });
  };

  const handleDeleteTemplateGroup = async (vendorId, groupLabel) => {
    if (!isCustomTemplateGroup(customTemplateGroups, vendorId, groupLabel)) return;

    const fallbackGroupLabel = getVendorTemplateGroups(selectedVendor).some((group) => group.label === "Templates")
      ? "Templates"
      : getVendorTemplateGroups(selectedVendor).find((group) => group.label !== groupLabel)?.label || "Templates";

    const nextGroups = customTemplateGroups.filter((group) => !(
      (group.vendorId || createVendorIdFromLabel(group.vendorLabel)) === vendorId
      && String(group.label || "").trim() === String(groupLabel || "").trim()
    ));
    const nextTemplates = customTemplates.map((template) => {
      const templateVendorId = template.vendorId || createVendorIdFromLabel(template.vendorLabel);
      if (templateVendorId !== vendorId || (template.groupLabel || "Templates") !== groupLabel) {
        return template;
      }
      return { ...template, groupLabel: fallbackGroupLabel };
    });

    try {
      await Promise.all([
        api.delete(`/api/generator/template-groups/${encodeURIComponent(vendorId)}/${encodeURIComponent(groupLabel)}`),
        ...customTemplates
          .filter((template) => (
            getCustomTemplateVendorId(template) === vendorId
            && (template.groupLabel || "Templates") === groupLabel
          ))
          .map((template) => api.post("/api/generator/templates", {
            vendorId,
            vendorLabel: template.vendorLabel || selectedVendor?.label || "Custom",
            name: template.name,
            previousName: template.name,
            groupLabel: fallbackGroupLabel,
            detail: template.detail || "",
            fields: template.fields || [],
            outputTemplate: template.outputTemplate || "",
          })),
      ]);
    } catch (error) {
      setCustomTemplateGroupError(error.response?.data?.error || error.message || "Could not delete section.");
      return;
    }

    setCustomTemplateGroups(nextGroups);
    setCustomTemplates(nextTemplates);

    const nextPrefs = { ...templateOrderPrefs };
    delete nextPrefs[buildTemplateOrderKey(vendorId, groupLabel)];
    writeTemplateOrderPrefs(nextPrefs);
    setTemplateOrderPrefs(nextPrefs);
  };

  const confirmPendingDelete = async () => {
    if (!pendingDelete) return;
    const { type, vendorId, templateName } = pendingDelete;
    setPendingDelete(null);
    if (type === "custom-template") {
      await handleDeleteCustomTemplateByName(vendorId, templateName);
    } else if (type === "built-in-template") {
      handleDeleteBuiltInTemplate(vendorId, templateName);
    } else if (type === "template-group") {
      await handleDeleteTemplateGroup(vendorId, templateName);
    }
  };

  const handleTemplateDragStart = (vendorId, groupLabel, templateName, event) => {
    const templateKey = buildDraggableTemplateKey(vendorId, groupLabel, templateName);
    setDraggedTemplateKey(templateKey);
    setTemplateDropTargetKey(templateKey);
    if (event?.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", templateKey);
    }
  };

  const handleTemplateDragEnd = () => {
    setDraggedTemplateKey("");
    setTemplateDropTargetKey("");
  };

  const persistTemplateGroupMove = async (vendorId, templateName, targetGroupLabel) => {
    const movedTemplate = customTemplates.find((template) => (
      getCustomTemplateVendorId(template) === vendorId && template.name === templateName
    ));
    if (!movedTemplate) return;

    await api.post("/api/generator/templates", {
      vendorId,
      vendorLabel: movedTemplate.vendorLabel || selectedVendor?.label || "Custom",
      name: movedTemplate.name,
      previousName: movedTemplate.name,
      groupLabel: targetGroupLabel,
      detail: movedTemplate.detail || "",
      fields: movedTemplate.fields || [],
      outputTemplate: movedTemplate.outputTemplate || "",
    });

    const nextTemplates = customTemplates.map((template) => {
      const isMatch = getCustomTemplateVendorId(template) === vendorId && template.name === templateName;
      if (!isMatch) return template;
      return { ...template, groupLabel: targetGroupLabel };
    });
    const changed = nextTemplates.some((template, index) => template !== customTemplates[index]);
    if (changed) {
      setCustomTemplates(nextTemplates);
    }
  };

  const handleTemplateDrop = async (vendorId, groupLabel, targetTemplateName, event) => {
    event.preventDefault();
    const sourceKey = draggedTemplateKey || event?.dataTransfer?.getData("text/plain") || "";
    const targetKey = targetTemplateName
      ? buildDraggableTemplateKey(vendorId, groupLabel, targetTemplateName)
      : `group::${buildTemplateOrderKey(vendorId, groupLabel)}`;
    setTemplateDropTargetKey("");
    if (!sourceKey || sourceKey === targetKey) {
      setDraggedTemplateKey("");
      return;
    }

    const source = parseDraggableTemplateKey(sourceKey);
    if (!source || source.vendorId !== String(vendorId || "").trim().toLowerCase()) {
      setDraggedTemplateKey("");
      return;
    }

    const sourceGroup = selectedVendorTemplateGroups.find((entry) => (
      buildTemplateOrderKey(vendorId, entry.label) === buildTemplateOrderKey(source.vendorId, source.groupLabel)
    ));
    const targetGroup = selectedVendorTemplateGroups.find((entry) => entry.label === groupLabel);
    const sourceGroupLabel = sourceGroup?.label || source.groupLabel;
    const sourceTemplates = Array.isArray(sourceGroup?.templates) ? [...sourceGroup.templates] : [];
    const targetTemplates = sourceGroupLabel === groupLabel ? sourceTemplates : Array.isArray(targetGroup?.templates) ? [...targetGroup.templates] : [];
    const sourceTemplateName = sourceTemplates.find((template) => (
      buildDraggableTemplateKey(vendorId, sourceGroupLabel, template) === sourceKey
    ));
    const sourceIndex = sourceTemplates.findIndex((template) => template === sourceTemplateName);
    const targetIndex = targetTemplateName
      ? targetTemplates.findIndex((template) => template === targetTemplateName)
      : targetTemplates.length;
    if (!sourceTemplateName || sourceIndex < 0 || targetIndex < 0) {
      setDraggedTemplateKey("");
      return;
    }

    const nextSourceTemplates = [...sourceTemplates];
    nextSourceTemplates.splice(sourceIndex, 1);

    const nextTargetTemplates = sourceGroupLabel === groupLabel ? nextSourceTemplates : [...targetTemplates];
    const adjustedTargetIndex = sourceGroupLabel === groupLabel && sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
    nextTargetTemplates.splice(adjustedTargetIndex, 0, sourceTemplateName);

    const nextPrefs = { ...templateOrderPrefs };
    nextPrefs[buildTemplateOrderKey(vendorId, sourceGroupLabel)] = nextSourceTemplates;
    nextPrefs[buildTemplateOrderKey(vendorId, groupLabel)] = nextTargetTemplates;
    writeTemplateOrderPrefs(nextPrefs);
    setTemplateOrderPrefs(nextPrefs);
    if (sourceGroupLabel !== groupLabel) {
      try {
        await persistTemplateGroupMove(vendorId, sourceTemplateName, groupLabel);
      } catch {
        // Keep the ordering preference local even if the backend update fails.
      }
    }
    setDraggedTemplateKey("");
  };

  const closeSaveDialog = () => {
    if (savePending) return;
    setSaveDraft(null);
    setSaveErrorMessage("");
    setSaveSuccessMessage("");
  };

  const handleSaveGeneratedNote = async () => {
    if (!saveDraft?.generatedConfig) return;
    const entryTitle = saveForm.entryTitle.trim();
    const folderPath = saveForm.folderPath.trim();
    const tags = saveForm.tags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean)
      .slice(0, 12);
    const savedAt = new Date();
    const renderedNote = buildGeneratedNoteMarkup({
      draft: saveDraft,
      userLabel: currentUserLabel,
      savedAt,
    });

    if (saveMode !== "existing" || saveForm.existingAction !== "append-entry") {
      if (!entryTitle) {
        setSaveErrorMessage("Note title is required.");
        return;
      }
    }
    if (saveMode === "existing" && !saveForm.groupId) {
      setSaveErrorMessage("Choose an existing title.");
      return;
    }
    if (saveMode === "existing" && saveForm.existingAction === "append-entry" && !saveForm.entryId) {
      setSaveErrorMessage("Choose a note to append.");
      return;
    }
    if (saveMode === "new" && !saveForm.groupTitle.trim()) {
      setSaveErrorMessage("New title name is required.");
      return;
    }

    setSavePending(true);
    setSaveErrorMessage("");
    setSaveSuccessMessage("");

    try {
      let savedGroupId = saveForm.groupId;
      let savedGroupTitle = saveForm.groupTitle.trim();
      let savedFolderPath = folderPath;
      let savedExistingAction = saveForm.existingAction;
      let savedEntryId = "";

      if (saveMode === "existing" && saveForm.existingAction === "append-entry") {
        if (!selectedAppendEntry) {
          throw new Error("Choose a valid note to append.");
        }
        const mergedTags = [...new Set([...(selectedAppendEntry.tags || []), ...tags])];
        const nextNote = selectedAppendEntry.note?.trim()
          ? `${selectedAppendEntry.note}\n\n${renderedNote}`
          : renderedNote;
        await api.put(`/api/note-entries/${selectedAppendEntry.id}`, {
          title: selectedAppendEntry.title,
          folderPath: selectedAppendEntry.folder_path || "",
          note: nextNote,
          status: selectedAppendEntry.status || "active",
          tags: mergedTags,
        });
        savedGroupTitle = selectedSaveTarget?.title || savedGroupTitle;
        savedFolderPath = selectedAppendEntry.folder_path || "";
        savedEntryId = selectedAppendEntry.id;
      } else if (saveMode === "existing") {
        await api.post(`/api/notes/${saveForm.groupId}/entries`, {
          title: entryTitle,
          folderPath,
          note: renderedNote,
          status: "active",
          tags,
        });
        savedGroupTitle = selectedSaveTarget?.title || savedGroupTitle;
      } else {
        const res = await api.post("/api/notes", {
          title: saveForm.groupTitle.trim(),
          entryTitle,
          folderPath,
          note: renderedNote,
          status: "active",
          tags,
        });
        savedGroupId = res.data?.id || "";
        savedGroupTitle = res.data?.title || saveForm.groupTitle.trim();
        savedExistingAction = "new-entry";
      }

      writeGeneratorSavePrefs({
        global: {
          saveMode: savedGroupId ? "existing" : saveMode,
          groupId: savedGroupId,
          groupTitle: savedGroupTitle,
          folderPath: savedFolderPath,
          existingAction: savedExistingAction,
          entryId: savedEntryId,
        },
        templates: {
          ...(readGeneratorSavePrefs()?.templates || {}),
          [buildGeneratorTemplateKey(saveDraft.vendorLabel, saveDraft.templateTitle)]: {
            saveMode: savedGroupId ? "existing" : saveMode,
            groupId: savedGroupId,
            groupTitle: savedGroupTitle,
            entryTitle,
            folderPath: savedFolderPath,
            tags: saveForm.tags.trim(),
            existingAction: savedExistingAction,
            entryId: savedEntryId,
          },
        },
      });

      setSaveSuccessMessage(saveMode === "existing" && saveForm.existingAction === "append-entry" ? "Generated config appended." : "Generated config saved.");
      window.setTimeout(() => {
        setSaveDraft(null);
        setSaveSuccessMessage("");
      }, 900);
    } catch (error) {
      setSaveErrorMessage(error.response?.data?.error || error.message || "Could not save generated config.");
    } finally {
      setSavePending(false);
    }
  };

  return (
    <section className="editor-panel config-generator-panel">
      <div className="section-heading section-heading-compact">
        <div>
          <span className="eyebrow">Config Generator</span>
          <h2>{selectedVendor ? selectedVendor.label : "Vendor Templates"}</h2>
        </div>
        {selectedVendor ? (
          <div className="section-actions section-actions-compact">
            <button
              type="button"
              className="action-button"
              onClick={openCreateTemplateDialog}
            >
              Create template
            </button>
            <button
              type="button"
              className="action-button action-button-secondary"
              onClick={openCreateTemplateGroupDialog}
            >
              Create section
            </button>
            <button
              type="button"
              className="action-button action-button-secondary"
              onClick={() => {
                setSelectedVendorId("");
                setSelectedTemplate("");
                setTemplateQuery("");
              }}
            >
              Back to vendors
            </button>
          </div>
        ) : (
          <div className="section-actions section-actions-compact">
            <button
              type="button"
              className="action-button"
              onClick={openCreateTemplateDialog}
            >
              Create template
            </button>
            <button
              type="button"
              className="action-button action-button-secondary"
              onClick={openCreateTemplateGroupDialog}
            >
              Create section
            </button>
          </div>
        )}
      </div>

      <p className="config-generator-intro">
        {selectedVendor
          ? `Choose a ${selectedVendor.label} template to open the next generator workflow.`
          : "Admin-only generator library grouped by vendor and template family."}
      </p>

      {!customTemplateLibraryReady ? (
        <div className="generator-template-empty">
          Loading generator templates...
        </div>
      ) : null}

      {customTemplateLibraryReady && !selectedVendor ? (
        <div className="generator-vendor-grid">
          {generatorCatalog.map((vendor) => (
            <button
              key={vendor.id}
              type="button"
              className="generator-vendor-card"
              onClick={() => {
                setSelectedVendorId(vendor.id);
                setSelectedTemplate("");
                setTemplateQuery("");
              }}
            >
              <span className="eyebrow">Vendor</span>
              <strong>{vendor.label}</strong>
              <span>{getVendorTemplates(vendor).length} template{getVendorTemplates(vendor).length === 1 ? "" : "s"}</span>
              <p>{vendor.detail}</p>
            </button>
          ))}
        </div>
      ) : customTemplateLibraryReady ? (
        <div className="generator-template-shell">
          <div className="generator-template-list">
            <label className="generator-template-search">
              <span className="eyebrow">Search Templates</span>
              <input
                value={templateQuery}
                onChange={(event) => setTemplateQuery(event.target.value)}
                placeholder={`Filter ${selectedVendor.label} templates`}
              />
            </label>
            {filteredVendorTemplateGroups.length ? (
              filteredVendorTemplateGroups.map((group) => (
                <section
                  key={group.label}
                  className={`generator-template-group${templateDropTargetKey === buildTemplateGroupDropKey(selectedVendorId, group.label) ? " is-group-drop-target" : ""}`}
                  onDragOver={(event) => {
                    if (!draggedTemplateKey) return;
                    event.preventDefault();
                    if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
                    setTemplateDropTargetKey(buildTemplateGroupDropKey(selectedVendorId, group.label));
                  }}
                  onDragLeave={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                      if (templateDropTargetKey === buildTemplateGroupDropKey(selectedVendorId, group.label)) {
                        setTemplateDropTargetKey("");
                      }
                    }
                  }}
                  onDrop={(event) => handleTemplateDrop(selectedVendorId, group.label, null, event)}
                >
                  <div
                    className={`generator-template-group-label${templateDropTargetKey === buildTemplateGroupDropKey(selectedVendorId, group.label) ? " is-group-drop-target" : ""}`}
                    onDragOver={(event) => {
                      if (!draggedTemplateKey) return;
                      event.preventDefault();
                      if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
                      setTemplateDropTargetKey(buildTemplateGroupDropKey(selectedVendorId, group.label));
                    }}
                    onDragLeave={() => {
                      if (templateDropTargetKey === buildTemplateGroupDropKey(selectedVendorId, group.label)) {
                        setTemplateDropTargetKey("");
                      }
                    }}
                    onDrop={(event) => handleTemplateDrop(selectedVendorId, group.label, null, event)}
                  >
                    <span>{group.label}</span>
                    {selectedVendorCustomGroupLabels.has(group.label) ? (
                      <button
                        type="button"
                        className="action-link danger generator-template-group-delete-link"
                        onClick={(event) => {
                          event.stopPropagation();
                          requestTemplateDelete("template-group", selectedVendorId, group.label);
                        }}
                      >
                        Delete
                      </button>
                    ) : null}
                  </div>
                  <div
                    className={`generator-template-group-items${templateDropTargetKey === buildTemplateGroupDropKey(selectedVendorId, group.label) ? " is-group-drop-target" : ""}`}
                    onDragOver={(event) => {
                      if (!draggedTemplateKey) return;
                      event.preventDefault();
                      if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
                      setTemplateDropTargetKey(buildTemplateGroupDropKey(selectedVendorId, group.label));
                    }}
                    onDragLeave={() => {
                      if (templateDropTargetKey === buildTemplateGroupDropKey(selectedVendorId, group.label)) {
                        setTemplateDropTargetKey("");
                      }
                    }}
                    onDrop={(event) => handleTemplateDrop(selectedVendorId, group.label, null, event)}
                  >
                    {!group.templates.length ? <div className="generator-template-group-drop-hint">Drop templates here</div> : null}
                    {group.templates.map((template) => {
                      const customTemplate = customTemplates.find((entry) => (
                        getCustomTemplateVendorId(entry) === selectedVendorId
                        && (entry.groupLabel || "Templates") === group.label
                        && entry.name === template
                      ));
                      const templateDragKey = buildDraggableTemplateKey(selectedVendorId, group.label, template);
                      const isDragging = templateDragKey === draggedTemplateKey;
                      const isDropTarget = templateDragKey === templateDropTargetKey;
                      return (
                        <div
                          key={template}
                          className={`generator-template-row is-draggable${isDragging ? " is-dragging" : ""}${isDropTarget ? " is-drop-target" : ""}`}
                          draggable
                          onDragStart={(event) => handleTemplateDragStart(selectedVendorId, group.label, template, event)}
                          onDragEnd={handleTemplateDragEnd}
                          onDragOver={(event) => {
                            if (!draggedTemplateKey || draggedTemplateKey === templateDragKey) return;
                            event.stopPropagation();
                            event.preventDefault();
                            if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
                            setTemplateDropTargetKey(templateDragKey);
                          }}
                          onDragLeave={(event) => {
                            event.stopPropagation();
                            if (templateDropTargetKey === templateDragKey) {
                              setTemplateDropTargetKey("");
                            }
                          }}
                          onDrop={(event) => {
                            event.stopPropagation();
                            handleTemplateDrop(selectedVendorId, group.label, template, event);
                          }}
                        >
                          <button
                            type="button"
                            className={`generator-template-link ${selectedTemplate === template ? "is-active" : ""}`}
                            onClick={() => setSelectedTemplate(template)}
                            title="Drag to reorder templates"
                          >
                            <span className="generator-template-drag-handle" aria-hidden="true">::</span>
                            <span>{template}</span>
                          </button>
                          <div className="generator-template-row-actions">
                            {customTemplate ? (
                              <>
                              <button
                                type="button"
                                className="action-link generator-template-edit-link"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openEditTemplateDialog(customTemplate);
                                }}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="action-link danger generator-template-delete-link"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  requestTemplateDelete("custom-template", selectedVendorId, template);
                                }}
                              >
                                Delete
                              </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="action-link generator-template-edit-link"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openEditBuiltInTemplateDialog(selectedVendorId, selectedVendor.label, group.label, template);
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="action-link danger generator-template-delete-link"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    requestTemplateDelete("built-in-template", selectedVendorId, template);
                                  }}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                            </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))
            ) : (
              <div className="generator-template-empty">
                No templates match <strong>{templateQuery}</strong>.
              </div>
            )}
          </div>

          <div className="generator-template-detail">
            {selectedTemplate ? selectedCustomTemplate ? (
              <>
                <span className="eyebrow">Selected Template</span>
                <h3>{selectedTemplate}</h3>
                <p>{selectedCustomTemplate.detail || "Admin-created custom template."}</p>
                <div className="section-actions section-actions-compact">
                  <button
                    type="button"
                    className="action-button"
                    onClick={() => openEditTemplateDialog(selectedCustomTemplate)}
                  >
                    Edit template
                  </button>
                  <button
                    type="button"
                    className="action-button danger"
                    onClick={handleDeleteCustomTemplate}
                  >
                    Delete template
                  </button>
                </div>
                <CustomTemplateGenerator template={selectedCustomTemplate} onClose={() => setSelectedTemplate("")} onSaveRequest={openSaveDialog} />
              </>
            ) : selectedRecoveredTemplate ? (
              <>
                <span className="eyebrow">Selected Template</span>
                <h3>{selectedTemplate}</h3>
                <p>{selectedRecoveredTemplate.detail}</p>
                <CustomTemplateGenerator template={selectedRecoveredTemplate} onClose={() => setSelectedTemplate("")} onSaveRequest={openSaveDialog} />
              </>
            ) : isPortMirror7210 ? (
              <>
                <span className="eyebrow">Selected Template</span>
                <h3>{selectedTemplate}</h3>
                <p>Ported from the original HTML/JavaScript generator into the current app.</p>
                <PortMirror7210Generator onClose={() => setSelectedTemplate("")} onSaveRequest={openSaveDialog} />
              </>
            ) : isBofConfiguration ? (
              <>
                <span className="eyebrow">Selected Template</span>
                <h3>{selectedTemplate}</h3>
                <p>Ported from the original HTML/JavaScript generator into the current app.</p>
                <BofGenerator onClose={() => setSelectedTemplate("")} onSaveRequest={openSaveDialog} />
              </>
            ) : isAccessPortConfiguration ? (
              <>
                <span className="eyebrow">Selected Template</span>
                <h3>{selectedTemplate}</h3>
                <p>Ported from the original HTML/JavaScript generator into the current app.</p>
                <AccessPortGenerator onClose={() => setSelectedTemplate("")} onSaveRequest={openSaveDialog} />
              </>
            ) : isServicePortConfiguration ? (
              <>
                <span className="eyebrow">Selected Template</span>
                <h3>{selectedTemplate}</h3>
                <p>Ported from the original HTML/JavaScript generator into the current app.</p>
                <ServicePortGenerator onClose={() => setSelectedTemplate("")} onSaveRequest={openSaveDialog} />
              </>
            ) : isBgpConfiguration ? (
              <>
                <span className="eyebrow">Selected Template</span>
                <h3>{selectedTemplate}</h3>
                <p>Ported from the original HTML/JavaScript generator into the current app.</p>
                <BgpGenerator onClose={() => setSelectedTemplate("")} onSaveRequest={openSaveDialog} />
              </>
            ) : isSnmpConfiguration ? (
              <>
                <span className="eyebrow">Selected Template</span>
                <h3>{selectedTemplate}</h3>
                <p>Ported from the original HTML/JavaScript generator into the current app.</p>
                <SnmpGenerator onClose={() => setSelectedTemplate("")} onSaveRequest={openSaveDialog} />
              </>
            ) : isSystemConfiguration ? (
              <>
                <span className="eyebrow">Selected Template</span>
                <h3>{selectedTemplate}</h3>
                <p>Ported from the original HTML/JavaScript generator into the current app.</p>
                <SystemGenerator onClose={() => setSelectedTemplate("")} onSaveRequest={openSaveDialog} />
              </>
            ) : isLoginControlConfiguration ? (
              <>
                <span className="eyebrow">Selected Template</span>
                <h3>{selectedTemplate}</h3>
                <p>Ported from the original HTML/JavaScript generator into the current app.</p>
                <LoginControlGenerator onClose={() => setSelectedTemplate("")} onSaveRequest={openSaveDialog} />
              </>
            ) : isTacacsAuthenticationConfiguration ? (
              <>
                <span className="eyebrow">Selected Template</span>
                <h3>{selectedTemplate}</h3>
                <p>Ported from the original HTML/JavaScript generator into the current app.</p>
                <TacacsGenerator onClose={() => setSelectedTemplate("")} onSaveRequest={openSaveDialog} />
              </>
            ) : isCpmFilterConfiguration ? (
              <>
                <span className="eyebrow">Selected Template</span>
                <h3>{selectedTemplate}</h3>
                <p>Ported from the original HTML/JavaScript generator into the current app.</p>
                <CpmFilterGenerator onClose={() => setSelectedTemplate("")} onSaveRequest={openSaveDialog} />
              </>
            ) : isIsisL2Configuration ? (
              <>
                <span className="eyebrow">Selected Template</span>
                <h3>{selectedTemplate}</h3>
                <p>Ported from the original HTML/JavaScript generator into the current app.</p>
                <IsisL2Generator onClose={() => setSelectedTemplate("")} onSaveRequest={openSaveDialog} />
              </>
            ) : isEthCfmServiceConfiguration ? (
              <>
                <span className="eyebrow">Selected Template</span>
                <h3>{selectedTemplate}</h3>
                <p>Ported from the original HTML/JavaScript generator into the current app.</p>
                <EthCfmServiceGenerator onClose={() => setSelectedTemplate("")} onSaveRequest={openSaveDialog} />
              </>
            ) : isVprnWithDhcpConfiguration ? (
              <>
                <span className="eyebrow">Selected Template</span>
                <h3>{selectedTemplate}</h3>
                <p>Ported from the original HTML/JavaScript generator into the current app.</p>
                <VprnDhcpGenerator onClose={() => setSelectedTemplate("")} onSaveRequest={openSaveDialog} />
              </>
            ) : isMxPortMirror ? (
              <>
                <span className="eyebrow">Selected Template</span>
                <h3>{selectedTemplate}</h3>
                <p>Ported from the original HTML/JavaScript generator into the current app.</p>
                <MxPortMirrorGenerator onClose={() => setSelectedTemplate("")} onSaveRequest={openSaveDialog} />
              </>
            ) : isQfxPortMirror ? (
              <>
                <span className="eyebrow">Selected Template</span>
                <h3>{selectedTemplate}</h3>
                <p>Ported from the original HTML/JavaScript generator into the current app.</p>
                <QfxPortMirrorGenerator onClose={() => setSelectedTemplate("")} onSaveRequest={openSaveDialog} />
              </>
            ) : isJuniperAcxPortMirror ? (
              <>
                <span className="eyebrow">Selected Template</span>
                <h3>{selectedTemplate}</h3>
                <p>Ported from the original HTML/JavaScript generator into the current app.</p>
                <AcxPortMirrorGenerator onClose={() => setSelectedTemplate("")} onSaveRequest={openSaveDialog} />
              </>
            ) : isAdvaEthCfm ? (
              <>
                <span className="eyebrow">Selected Template</span>
                <h3>{selectedTemplate}</h3>
                <p>Ported from the original HTML/JavaScript generator into the current app.</p>
                <AdvaEthCfmGenerator onClose={() => setSelectedTemplate("")} onSaveRequest={openSaveDialog} />
              </>
            ) : (
              <>
                <span className="eyebrow">Selected Template</span>
                <h3>{selectedTemplate}</h3>
                <p>
                  This template is grouped under {selectedVendor.label}. Its generator workflow has
                  not been ported yet.
                </p>
              </>
            ) : (
              <>
                <span className="eyebrow">Template List</span>
                <h3>{selectedVendor.label}</h3>
                <p>Select a template from the list to continue.</p>
              </>
            )}
          </div>
        </div>
      ) : null}

      <SaveGeneratorNoteDialog
        open={!!saveDraft}
        draft={saveDraft}
        titles={saveTargets}
        filteredTitles={filteredSaveTargets}
        folderOptions={saveFolderOptions}
        loadingTitles={saveTargetsLoading}
        loadingTargetEntries={saveTargetEntriesLoading}
        targetEntries={saveTargetEntries}
        titleQuery={saveTitleQuery}
        setTitleQuery={setSaveTitleQuery}
        saveMode={saveMode}
        setSaveMode={setSaveMode}
        form={saveForm}
        setForm={setSaveForm}
        saving={savePending}
        errorMessage={saveErrorMessage}
        successMessage={saveSuccessMessage}
        onClose={closeSaveDialog}
        onSubmit={handleSaveGeneratedNote}
      />
      <CustomTemplateDialog
        open={showCreateTemplateDialog}
        form={customTemplateForm}
        setForm={setCustomTemplateForm}
        availableGroupLabels={selectedVendorGroupLabels}
        errorMessage={customTemplateError}
        onClose={closeCreateTemplateDialog}
        onSubmit={handleCreateTemplate}
        mode={customTemplateDialogMode}
      />
      <CreateTemplateGroupDialog
        open={showCreateTemplateGroupDialog}
        form={customTemplateGroupForm}
        setForm={setCustomTemplateGroupForm}
        errorMessage={customTemplateGroupError}
        onClose={closeCreateTemplateGroupDialog}
        onSubmit={handleCreateTemplateGroup}
      />
      <DeleteConfirmationDialog
        open={!!pendingDelete}
        itemLabel={pendingDelete?.templateName || "this item"}
        itemType={pendingDelete?.type === "template-group" ? "section" : "template"}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmPendingDelete}
      />
    </section>
  );
}
